import React from 'react';
import { Rect, Text } from 'react-konva';
import EImagingTaskSource from 'enums/EImagingTaskSource';
import Cookies from 'js-cookie';
import jwtDecode from 'jwt-decode';
import EImagingTaskState from 'enums/EImagingTaskState';
import {
  LayerDTO,
  ExpGlobalDataDTO,
  GlobalIngredientsDTO,
  IngredientIngredientType,
  IngredientStockDTO,
  WellIngDTO,
  IngredientTypeDTO,
  IngredientDTO,
  ScreenDTO,
  LayerTypeDTO,
  GridLayerDTO,
  IngredientTypeColorDTO,
  CustomLayerDTO,
  PlateTypeDTO,
  LayerStateDTO,
  TreeNodeDTO,
  PrintBarcodeApiResponse,
  PrintBarcodeClient,
  UserDTO,
  FileResponse,
  ProteinFormulationDTO,
  FavoriteIngredientStock,
  GlobalDataVersionDTO,
  GlobalDataComparisonRequestDTO,
  GlobalDataComparisonResultDTO,
  SeedingDTO,
  ResponseType,
  Entry,
} from 'business/models/Models';
import EColors from 'enums/EColors';
import EScoreType from 'enums/EScoreType';
import {
  GetGlobalComparisonDataService,
  GetGlobalDataService,
} from 'business/services/GlobalService';
import {
  DetailTextProps,
  RMRectangle,
  TextProps,
  WellDetailsStates,
} from '../business/models/CustomModels';
import { EGridVaryAlong, ELayersDefaultInput } from 'enums/ELayersView';
import { getIngredient } from 'business/plateviewer/Generate/GenerateCommon';
import { getLinkedLayerGridDto } from 'business/plateviewer/GenerateUtil';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import EAutoScoreType from 'enums/EAutoScoreType';
import EAutoScoreImageClass from 'enums/EAutoScoreImageClass';
import EAlertType from 'enums/EAlertType';
import { Root, createRoot } from 'react-dom/client';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import {
  RMSvgIconNames,
  RMSvgIconProps,
} from 'components/customComponents/CommonComponents/RMSvgIcon';
import { TreeService } from 'business/services/TreeService';
import ModelFactory from 'business/models/ModelFactory';
import RecentDocumentService from 'business/services/RecentDocumentService';
import { EUserAction } from 'constants/types';
import { GridLayerTypes } from 'components/commandpanel/Layers/LayersSelectionParent';
import { EFeatureTypes } from 'enums/EFeatureTypes';
import { useCanvasPreference } from 'redux/hooks/common';
import { ECanvasPreferencesKeys } from 'enums/ECanvasPreferencesKeys';
import {
  Containers,
  ExperimentInfoImagingScheduleDto,
} from 'components/Experiment/constants';
import { getPlainContainers } from 'transformers/experiment';
import { IndexDBGlobal } from './IndexDBGlobal';
import { GetGDataVersion } from 'business/services/GDataVersioningService';
import { PlateCanvasLabels } from 'components/canvas/constants';
import { CacheHelper } from './CacheHelper';
import connectionHubManager from 'connectionHub/connectionHubManager';
import { clearAuthStorage } from 'utils/authSession';
import { FmlxIcon, FmlxTable } from 'fmlx-common-ui';
import { AUTH_FLOW_PATHS, REDIRECT_URL_KEY } from 'components/main/constants';
import { NavigateFunction } from 'react-router';

let navigateFunc: NavigateFunction = null;
export const getNavigateFunction = () => {
  return navigateFunc;
};
export const setNavigateFunction = (navigate: NavigateFunction) => {
  navigateFunc = navigate;
};

declare global {
  interface Array<T> {
    remove(callback: CallableFunction);
    filterByIndexes(indexes: number[]): T[];
    removeByIndexes(indexes: number[]): T[];
    mergeByProperties(otherArray: T[], keys?: (keyof T)[]): T[];
    distinctBy(property: string): T[];
    groupByMultipleKeys(...columns: string[]): { [key: string]: T[] };
    distinctByMultipleProperties(properties: string[]): T[];
    swapItemsByIndex(indexA: number, indexB: number): T[];
  }
  interface String {
    truncate(length: number, appendEllipsis?: boolean, includingEllipsis?: boolean);
    formatPascalCaseToCamelCaseString(): string;
    formatPascalCaseToLowerCaseString(): string;
    equalsIgnoreCase(value: string): boolean;
  }
}

/**
 * @addEllipsisBackward: output of length given including elipsis, it will add ellipsis backward,
 * e.g: helloworld123.truncate(10, true, true) it will return hellowo...
 */

String.prototype.truncate = function (
  length: number,
  appendEllipsis = false,
  includingEllipsis = false,
) {
  const _length = includingEllipsis ? length - 3 : length;
  return this.length > length
    ? `${this.substring(0, _length)}${appendEllipsis ? '...' : ''}`
    : this;
};

String.prototype.formatPascalCaseToCamelCaseString = function () {
  return this.replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/^\w/, (match) => match.toUpperCase());
};

String.prototype.formatPascalCaseToLowerCaseString = function () {
  return this.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
};

Array.prototype.remove = function () {
  const a = arguments;
  let what,
    ax,
    L = a.length;

  while (L && this.length) {
    what = a[--L];
    while ((ax = this.indexOf(what)) !== -1) {
      this.splice(ax, 1);
    }
  }
  return this;
};

Array.prototype.filterByIndexes = function <T>(this: T[], indexes: number[]): T[] {
  const filteredArray: T[] = [];
  for (const index of indexes) {
    if (index >= 0 && index < this.length) {
      filteredArray.push(this[index]);
    }
  }
  return filteredArray;
};
Array.prototype.mergeByProperties = function <T>(
  otherArray: T[],
  keys?: (keyof T)[],
): T[] {
  const map: Record<string, T[]> = {};
  const generateKey = (obj: T) => {
    if (keys) {
      return keys.map((key) => obj[key]).join('_');
    } else {
      return JSON.stringify(obj);
    }
  };

  for (const obj of this) {
    const key = generateKey(obj);
    if (!map[key]) {
      map[key] = [obj];
    } else {
      map[key].push(obj);
    }
  }

  const mergedArrays = otherArray.map((obj) => {
    const key = generateKey(obj);
    const existingObjs = map[key] || [];

    // Merge the objects from the first array with the current object from the second array
    return existingObjs.map((existingObj) => ({ ...existingObj, ...obj }));
  });

  return [].concat(...mergedArrays);
};

Array.prototype.removeByIndexes = function <T>(indexes: number[]): T[] {
  const indexesToRemove = new Set(indexes);
  return this.filter((_, index) => !indexesToRemove.has(index));
};

Array.prototype.distinctBy = function (property: string) {
  const uniques = new Set();
  return this.filter((item) => {
    const value = item[property];
    if (!uniques.has(value)) {
      uniques.add(value);
      return true;
    }
    return false;
  });
};

Array.prototype.groupByMultipleKeys = function <T>(...columns: string[]) {
  return this.reduce((acc: { [key: string]: T[] }, obj: any) => {
    const key = columns.map((col) => obj[col]).join('-');
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(obj);
    return acc;
  }, {});
};
Array.prototype.distinctByMultipleProperties = function distinctByProperties(
  properties: string[],
) {
  const seen: { [key: string]: boolean } = {};
  return this.filter((item) => {
    const key = properties?.map((prop) => item[prop]).join('|');
    if (!seen[key]) {
      seen[key] = true;
      return true;
    }
    return false;
  });
};
export const headerButtonsColor = '#313131';
export const DropDownListButtonsColor = '#414042';
export const treeNodeIconsColor = '#414042';
export const headerIconSize: Omit<RMSvgIconProps, 'name'> = {
  width: 24,
  height: 24,
};

export const RMHelpURL = 'https://help.formulatrix.com/rock-maker/4.13';
/** @deprecated RMLiteHelpURL and RMHelpURL contains the same URL (used to be different URLs) */
export const RMLiteHelpURL = RMHelpURL;

export const customTabIconProps: Omit<RMSvgIconProps, 'name'> = {
  fill: '#474747',
  width: 34,
  height: 34,
};

let newId = 0;
export function getNewId(): number {
  return --newId;
}

export const isObjectEmpty = (obj: Record<string, unknown> | null | undefined) =>
  !obj || Object.keys(obj).length === 0;

export const generateUUID = (): string => {
  // Public Domain/MIT
  let d = new Date().getTime(); //Timestamp
  let d2 =
    (typeof performance !== 'undefined' && performance.now && performance.now() * 1000) ||
    0; //Time in microseconds since page-load or 0 if unsupported
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    let r = Math.random() * 16; //random number between 0 and 16
    if (d > 0) {
      //Use timestamp until depleted
      r = (d + r) % 16 | 0;
      d = Math.floor(d / 16);
    } else {
      //Use microseconds since page-load if supported
      r = (d2 + r) % 16 | 0;
      d2 = Math.floor(d2 / 16);
    }
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

let gdata: ExpGlobalDataDTO;
const indexDBGlobal = new IndexDBGlobal();
export const errorSrc =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAoAAAAKACAMAAAA7EzkRAAADAFBMVEUAAAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD/AAD///+P0z5UAAAA/nRSTlMAAQIDBAUGBwgJCgsMDQ4PEBESExQVFhcYGRobHB0eHyAhIiMkJSYnKCkqKywtLi8wMTIzNDU2Nzg5Ojs8PT4/QEFCQ0RFRkdISUpLTE1OT1BRUlNUVVZXWFlaW1xdXl9gYWJjZGVmZ2hpamtsbW5vcHFyc3R1dnd4eXp7fH1+f4CBgoOEhYaHiImKi4yNjo+QkZKTlJWWl5iZmpucnZ6foKGio6Wmp6ipqqusra6vsLGys7S1tre4ubq7vL2+v8DBwsPExcbHyMnKy8zNzs/Q0dLT1NXW19jZ2tvc3d7f4OHi4+Tl5ufo6err7O3u7/Dx8vP09fb3+Pn6+/z9/kvbU+4AAAABYktHRP+lB/LFAAAXmElEQVR42u3dh3+W5bnA8TuTTQJhBhDFhYCr1raKdXAQWq11VNRTT8FCjZVqHVWjWDW2igHtsdEWDagouMLwCNpiqTKEChIiCBhWGDKFkDBCBhn3p3KQCih5QvK+zz2u3+8/4Lq+5H2f8T6PUkREREREREREREREREREREREREREREREREREREREREREREREREREREREREREREREREREREREREREREREREREREREREQU6VJOOf+KX94zMnt8zvTZuas+LyoqqtIHqi4q2liwOjd3+qSXns64e8i1fXq1j2FeFJGa9rxsyCNj3l3yxUFtdatyU967Yx//7U/PaMkIqV4l/2DQo+PnbtENrTB34lO3XdYtlolSHWvT97fPfdBweYdXmvf676/tmch0qZZiT71u+LsbdfSq/OzVe/okM2j6Zl2ueeL9nTqU1kwc9uO2TJwOFv/9uyZv0SG3fOyvenCsTE36PvpBiTbUjneGXdiYHYgt7pz06WXacKVzMvsmsAt5dfvNO7u1Je2YeOspbERQjfs9vVxb1rpRP+LTWEQpgyaXaCsrmTy4HfvxuxPunFGlLa563rCebMnXjrtjTo22v2UZp7Er/+rshr6DBruzMZ9Kvnm2O/oOlHd/Z/bmyem+vuNKtINVz0lrxvacr+eft2lnK37+B2zQ5Zr98l/a8fLTO7BHR/vO87u0B1W8/kN26V6JA6Zrb1qezk2EbtUxfYP2qt3Zp7NVZ/p+TqX2rprp/bl/0IVir5iuPW3xQG7csr2mQ1dpj1t/Vwt2bHFJw7ZpzysezmkZW2uTUaQFVJHdiV1bWOrTJVpIZc9yndi6v36ZpVpQFeNOZOcW1Xr4Hi2silEd2bsltcjYpQW2dziXR2woIW2LFlpRehP2b7orVmrBbUyLh4DJLpynhZd/GQqM1X2KJj2tBxKM1CyjHH37q8zm58ThF3PTFuj952jkTu5SCLlz5sLu0FZeiokQa/9iNeaOuF1wPDcphPbpO3A74L7ZzjvisBFGJ78Ptm/vk/PQEf0LH+kc+x79czib+1WjfeY5H2a1tbY/RqJY0os1GAvo5dY4iVb9PsdXcFsHIiUqtRjNn7+6NaENWiLfD1chq659cSVeIlyzUfz5O5ZGN8dMRK+8rcDUMR4OX4SaiBV7XwWijrWqTG5QiFDt/w6n+rTgJOxEoqsLsVS/dt+IngbXOBtI9e+FpghqWF3no6gh5Z+FoYZ0zU4MNayyISiqd/GZnPxreOP4GK5nneegJxLlnYCl+tRnG3YiUyH3aNWjOyqRE6mqH+Lx0sdYoxdhE8neaYmpYyn1I8xEtk+PR1Xd+856xET8i+DFuKprv+B3R1GoYjCy6lTMI5z9i07/yw+H61DiK0iJVtP41WZgrT7ASfT6uD3Cau+Ez1ASzdZ0x1htnbsVI1E+GD4fZUfv0j0IiXZlP8PZ0bqqDB/Rr+oWpH17t/LQv3DKxNq3lY6MsMri3oRvnn7+Ey7C6xVeL3JEcWNREWb/1xhzhxb/OibC7e8IPKSEiYgIu5k8Pebry79v4SH8ZiHwqxq9jQYTfcitCf9fk+lYMNNsBO7//P0bEkw1h09hFZeDA4Ofws3E+3sNBSZ7r5Hw6x9jMGC2t0RfE4l5DgGmmyD5hyJc/7WgMXLvTLiP7dvQCKn+/oefX9rR3TL9Xc7ThyypZpBEf98vYfO2tO9H8vz12MHe7WnPudL8dVjH1m1qm7B3ijTh8WuWtVrUGzZjJzm2nqo1MyaMHpGZ+Yf09PTb0tLS7k4/2GOZX5b9ZS/lfNnU6V82Pzc3d2FBgWOPmJuRKAjgk475uz+pHv/I4x37R46V42+Ia59PnZQAgHqYFH/9nDsB2FYEwJrrZPg7udi5b+hJIgDqUhEnY1osde8QsYkMgHpzqoA7sCY4eI4iTghAPdf/Q+FHHVxLlZICUI/y3d+VLj4Bq1QOQJ3m+RXg3S4uZacggOXn+eyveb6bF0oFAdSbfH6OuaM/gdskCaD+p78/Ehnq6Eo+FwVQZ/jq78xSRzeyThbAak9fL5xc4OpG1soCqLd18tFfzCRnF7JGGED9UYKHAG93dx8F0gDq4f7561nq7jpWiQNY3cc3f40WObyOFeIA6o2tPQOY5fI2lssDqCf55a+f0w9ByBcIUN/kk7+2m53exTKJAEtO8Qig408hXyoRoJ7nz5MDBzm+iiUiAeoHfPGXWuT4JhbLBFh+uicAnX8M/iKZAHWeHxdEfuX+IoQC1A/64K9TsfN7WCgVYIUHH8Ix77m/h1ypAPVC94+EB3qwho/FAtT3uu4v5QsPtjBfLsC93RwH+LIPW/hILkA9zW1/F3nxIPx/CQaob3D6Jqx8L3YwVzLAra0cBviYHzv4UDJAPdpdf6dW+LGC2aIB1vTmGpzhZokGqPNc/aX6lb5sYKZsgPoWR49AVvqygBnCAe5IcRLgwxqAnvQXF/11KQGgL1Wd6SDACRqA3jTLPX/n1QDQo65x7i6suRqAHlXg2tPLr9MA9GkE+ja3/CWuAqBfALcnOQXwTg1AvwDqx13yl1wIQN8Alh7nEMBMDUDfAOqX3PHXYS8A/QNY1d0ZgFkagP4B1K+64i+1FIA+Aqw+wxGA2RqAPgLUE93wd3wFAP0EWOPGC63HaQD6CVBPdcHfKVUA9BWgduFP4EsagN4CnOzAfagVAPQXYE0v6wE+owHoL0A93nZ/7fYC0GeAVSdZDvAJDUCfAern7PaXVAxAvwGWp1oN8H4NQL8B6pE2+0vYAEDfARY3txjgLzQAfQdo9a9DPgag/wBXxVrr7xINQP8B6p9aC3AKACUAnGGrv5OrmbgEgPq7lgL8M//lZQC09OdJTYoAKANgaWsrAQ7RAJQBUN9pJcAFAJQCMD/GQn/nagBKAagv4U5oAJrsDfv8Je8FoByA++y7J+Y2DUA5APUD1gFcCEBJAFfYdhjSSwNQEkB9nmUAnwagLIDPW3Yn6jYAygK4s4lVAK/WAJQF0LIXWU8BoDSAf7fJX9tKAEoDWNXJIoBDNQClAdR3WQRwNgDlAZxnj7/UagDKA6hPtAbg7zQABQK8nzsBAWiyT2zxd2INACUC1KdZAnCYBqBIgA9bAjAXgDIBLrbDX6caAMoEqLtZAfB2DUChAO04F/0BAKUCnGmDv9aVAJQKsKqNBQAHaQBKBagHWQBwMgDlArTgxTWJuwEoF2BJY+MAL9EAlAtQ9zUOcAQAJQN80jjATwEoGeCnpv11rAGgZIC6i2GAQzQARQP8pWGAEwAoG+CbZv3FFgJQNsAdcUYBnqUBKBugPscowLsBKB3g3UYBTgWgdIBvm/QXVwxA6QCLTX4J/J4GoHSA+iyDANMBCECTLw15B4AAfMucv5hCAAKw0NzjortrAAJQn2oM4GAAAtDkffljAAhArUcZA7gUgADUOs+Uv+RqAALwyx9nNjME8FINQAB+2YWGAN4PQADu7z5DAHMACMD9TTAEcLWUAc8EYK2tMnQMUgNAAO6vpqURgH3EDHgWAGuvtxGA94iZ72wA1t5QIwBfFTPfDwFYe9lGAC4BIAAPNN+Ev/hyMfOdA8DaKzVxW353OfOdC8CAuhsA+DM54/0XAAO6ygDAhwAIwIOZeG3c63LG+xEAAxprAOBiOeOdB8Co/BdtULFlnGUA4MGKwwfYVdB4PwZgUB1CB/hfgqa7AIBBXRw6wDRB080FYFC3hA5wBAAB+HUjQgc4SdB0FwIwqDdCB7hI0HTzABidM1UNaY+g6X4CwKC2hu2vjQYgAL+upmnIAM+SNN1FAAws7PthfiJpuIsBGNiPQgb4a0nD/RSA1p0I/IOk4S4BYGCPhQxwLAABeGhjQgY4XdJwlwIwsKkhA1wmabjLABjYgpABbpc03M8AGNiGcP3FVUkabj4AA9sXGyrAthqAADysNqEC7CFqtssBGFyvUAFeKGq2KwAYXJ9QAV4rarYrARjcNVyJi1qrABjc4FABPiBqtqsBGFy4b04fDkAAHt6joQJ8VtRsCwAYXFaoAF8RNds1AAzulVABThY127UADO7tUAH+U9Rs1wEwuJmhApwPQAAeXrjPiRZ1N5ZeD8DgPg0VYIGo2X4OwOBWhApwo6jZbgBgtD4m6ts2UbPdCMDgvggV4C4AAvDwdoUKsEzUbDcBMLiKUAFWi5rtZgDWoTDvyU+QNdotAKxDCSECbCxrtFsBaBnARgAEoEmAiZxhAKBJgMK+A24DYB1KBGC02g5AywDGyxptIQAtAxgHQACaBBgja7Q7AFiH4sK8ErJP1GiLABhcZaiX4vaImm0xAIPbEypAUY8H1DsBGK1TBfVtg6jZ7gJgcBtDBbgKgAA8vNWhAlwiara7ARjcslABLuD7NQAPb2GoAGeKmm0JAIP7MFSAb4ua7V4ABhfui0JeASAAD298qACfETXbUgAG95dQAYp6V6EuA2Bw4b6t8HeiZlsOwODuDRXgr0TNtgKAwaWFClDWaxr2ATC460MFeDEAAXh4/UIFeJqo2VYCMLgzQgWYImq2VQAMrl2oAGNE3RJdDcDg/6Sh3pGv1CYAAvDQtoTrT30iabg1AAxsUcgAp4maLgADmxYywJdFTTcGgEGNCxmgrIvBsQAMakTIAG8GIAAPbWjIAPsDEICHdnnIAGVdCokDYFA9QwbYVNR04wEYVIuQAapCSdNNAGBAhWH7U3mSxtsIgAEtDB3gRAAC8OsmhQ5wuKTxNgZgQH8KHeBgAALw624JHeAFksbbBIABXRQ6wPaSxtsUgAF1CB2g2ilovM0AWHs7w/encgEIwIPNNwDwVUHzbQHA2htnAOADAATgwYYZAPgTQfNNAmDtXWMAYFcAAvBgJxoAGFMMQAAeaHeMAYBqrpwBJwOw1j404U89L2fArQBYa88aAfgbOQNOAWCt3WwE4PkABOCBvmsEYJN9AATg/qqaGgGoFomZcFsA1tZSM/7UCwAE4P5eNATw12Im3A6AtZVmCOA5YibcHoC1dYYhgInlUibcAYC1VBJvCKCaJ2XEHQFYSzNM+VNPARCAWg83BvAqKSNOBWAtXWkMYNsaISPuDMBIf0OOTPkABGC+OX9qNAABOMogwEFCZtwFgEfveoMAuwmZcVcAHrUag18BlVorY8g/7FaPLuQrYPQbo0l4o4wCvIEFSO96owDbVrMB2VW3MwpQ1pN66ZstMOtPjWQFsvujYYD9WYHsehsG2GQvO5BccbxhgGoKS5Bcjml/6haWILnBxgGm1rAFudV0Mg5QLWQNcss17089yhrk9qAFAM9lDXLrYQHA2M3sQWorlA39hUVIbbgVAC9mEVI71wqAfAZLbWOMFQDVc6xCZs/a4U/1YRUy620JwLgv2IXE1lvyCaxUNsuQ2BO2+OM4WGanWwMwZi3bkNdnyp6Gsw55DbMI4GmsQ1w1J1gEUC1gIdKaY5M/dQcLkdZgqwC2q2QjstrTwiqA6m1WIqsX7PKnrmAlsjrPMoBx69mJpJbHWAaQn4bI6ne2+VPHVbEVOVW0sw6g+htrkdNr9vlTV7MWOfW2EGD8BvYipU+UjQ1jMVIaYiXANqVsRkZFTa0EqF5gNTIaaac/1YsHZYmoupulANUMliOhKbb640yMjC6wFmBcAdvxv4+UvQ1lPf53lcUAG29hP763ItZigOpBFuR7N9vsTyXtZEN+t7Wx1QDVk6zI7x6w25/qUMaOfK442XKAajRL8rmHbfenulawJX/baf0fQJ7V5nWP2O9PHVfOnrz9A9jKAYAePjI6o36DyPBuEI+64E+llgLQT4C7WjsB0L831wDwQA+54U91KgWgjwC3NncEoHoCgD4CHOqKP5VUCED/AK5JdAaguguA/gG83h1/KnE1AH0DuCjWIYDqRgD6BvBSl/yp2DwA+gVwmnKrvgD0CmBlD8cAqskA9Alglmv+1AllAPRnAkUpzgFUjwHQnwn8xj1/quk6APrSsngHAar/BqAv9XfRn4r5EIB+NFm52ZmVAPSh3V0cBaieAqAP3eWqP9V0DQDdb3GCswDVjwHofNXnKYebBEDX+6vL/lTHnQB0uy3JTgNUtwPQ7a5125+KnQ1Al8tRrtetBIDutr298wB9+H2IXIA3uO9Pxc4CoKtNUT7UbQ8A3aywgxcA1Z0AdLMb/fDn/oewUIATlS91KQKge21K8QaguhaAzlXdV3nUeAC61lM++VPNVwLQrZY09gqgOncfAF2q7HTlWY8A0KVu882fip8FQHeaFOMdQNV+MwBdaV1r5WGXVAHQjcrPUV72MADdaKif/lTsNAC6UI7ytdbrAGh/q5K8Bah67wOg7e09U3ncrQC0vJoblNdlA9DuMv32pxJmAdDm/hHnOUDVfgMA7W1NivK+s/cC0Nb29FICGghAS6u+WokoE4B2li7Dn4p5FYA29pKSUuO5ALSvmYliAKq2BU6tZlzfejXOqX/kZ8lKUN0d/6Gmf20/SYnqwnJ2blNl5yth/byardtT1TVKXENZuzXV3KwE9hiLt6UHlcieYfN29FeZ/lRcDru3oddjhQJUie+xffP9s5ESW8v57N90c5srwSUtQIDZ8lop0SUvxIDJFqco4bVdigJzLW+vxNf+MxyYamVH/CnVuQAJZlrfFX3764pAI63G31d1WIaG8MvvhLz/fA/8FA9h90lb3B1yLLwIEeG2IAV1h50PnIeJMJvVEnNHXBOZjYrw+lsTxB1Zo0m4CKspjfH2LXdnvYiMcHotHm3fVsxT2Aij0bFYO0oPoSP6/TEGaEftpn0AiW6Vv0ZZbfXdhZFotucyjNXe6RtQEr02n42woDpxUSRqLTkOX8G1eh8p0ekfXP6oU/HPYiUajU3AVh1L42A44tVk4Kru9S9GTGTb+3NUHUvdV2Emkq05C1PHVsu3UBO5ZrRD1DFfGU7nCYKR+vqXGYenenQ5XwQjc/VjAJbq18n8aD0CreyFpPrW4g38NLQpSThqQAP3QqhhX/+4+a9hnb0SRfVvSz8ENfhj+DUc1fviL89+iUS38DFcr8p+y73PEboswkME61E+Fz8iVkIGJ6WPtXHNcBPB+myE1LFUfD1mIlvKm6iqex90RkzEu247sOpW6b2c/ItG7Xh4R52a0x0rUWpAIbyC2pvOn7/o1XEiwmrv3S4oiWo/+RxktRz8piEk2rXM4pzg0Zqaio8Q6s0zzb+1zVdhI6QLI/fugduR7XuaG//CK3Uc4o449cx9z+F2MbfrH9KGgYgIu8R7dwLvq1N/D/HMZxOlZFWCb/+x7/FYMFT3d+CXz133Jrtc+CmZjWn85txssQPWyOVXmM4LZyw4GknbKpPfnkzO/NlR0mMl8viVjuR1g/bUJkPY0/X3ZfO2X7tqN1LQX8Hqcd3YuH0EnxRCsDz7ZLZt6QexgDumd2fx4Wtvze7w/H7VrRmt2LLdJ2Vu8vjU9PIhjdiw/V2QU+Ulv7yBXPVwpO7PeXc8Uj6+N3t1qFb3rfaJ38p72rBTx4rtm+PJ+5aqpl/Bc9acrGP6Ovf5bcrkFZfuFnf5hHKX9VW++1MOPBwvOW2Oq/yWpXdgfz7Ua+R6B/X9nuu9HnVO1hanbnTOuoCd+fZ1sN9LjlwpLhx9MQ+48tPgBVkbbNdXkNWXF5t7XMz3nrD3F+2V7991Eivyv65pObvt01eUMzCZ3UipyY+fsemmmX1zH78onq0Iq92A7LU2XGjLzbyCX7dJ7eQhL68y+aVv7vB+vE9Geh0HZOUauG1h89SM/uCjAyX0HJi9LLRH/xbPyRrYk6HTEbW86PYxH0f5zZwb3/3DlbzFiI5e3KkDHnl9YeRvqC5d9OYfb/xuSwZMdeq4vkP/NDmvOALySvKn//X2fsdzSynVo1ZnX3X74y+/t3THsbKr2bJwyqiHBvXrwRkWikSNUk+/5Npbf/9k9htTP8hdubaoqPTwP3NFRQXL5733ZvaIB4b+/PLevTonMjKK+kFLqwM1ZxRERERERERERERERERERERERERERERERERERERERERERERERERERERERERERERERERERERERERERERERERERERERCSmfwMFab3Q8/4jlwAAAABJRU5ErkJggg==';
export function getHexColor(color: number): string {
  return `#${color.toString(16).padStart(6, '0').toUpperCase()}`;
}
export function getCurrentYear() {
  const date = new Date();
  return date.getFullYear();
}

export function SetGlobalDataInMemory(gdata: ExpGlobalDataDTO) {
  SetIngredients(gdata.ingredientData);
  SetFavouriteStocks(gdata.ingredientData.favouriteIngredientStocks);
  SetIngredientTypeColors(gdata.ingredientData.ingredientTypeColor);
  SetScoreDefn(gdata.scoreDefn);
  SetIncubTemps(gdata.incubationTemps);
  SetSetupTemps(gdata.setupTemps);
  SetContainers(getPlainContainers(gdata.containers));
  SetUsers(gdata.users);
  SetCaptureProfileTuningValues(gdata.captureProfileTuningValues);
  SetSeedList(gdata.seedList);
  SetSeedDict(gdata.seedList);
  SetScreenList(gdata.screenList);
  SetProteinFormulationList(gdata.proteinFormulationList);
  SetProteinFormulationDict(gdata.proteinFormulationList);
  SetLicenseEdition(gdata.licenseEdition);
  setIngredientByIngredientIdDict();
  setIngredientStockByIngredientStockIdDict();
  setIngredientIngredientTypeByIngIngTypeIdDict();
  setIngredientTypeByIngTypeIdDict();
  setIngredientTypeColorByIngColorIdDict();
}

export async function SetGlobalExpData(forceFetch = false) {
  const gScores = GetScoreDefn();
  if (gScores === null || gScores === undefined || forceFetch) {
    gdata = await SetGlobalExpDataIndexDb();
    SetGlobalDataInMemory(gdata);
    return gdata;
  }
  return gScores;
}

export async function SetGlobalExpDataIndexDb() {
  const gdataIndexDBVersion = await indexDBGlobal.ReadVersion();

  if (gdataIndexDBVersion != null) {
    const gDataVersion: GlobalDataVersionDTO = await GetGDataVersion();
    const VersionComparisonResult = compareVersions(gdataIndexDBVersion, gDataVersion);
    const isgDataFetchRequired = HasAnyValueFalse(VersionComparisonResult);
    if (isgDataFetchRequired) {
      const globalDataComparisonRequestDTO = new GlobalDataComparisonRequestDTO({
        comparisonResult: new GlobalDataComparisonResultDTO({
          ...VersionComparisonResult,
        }),
      });
      try {
        const data = await GetGlobalComparisonDataService(globalDataComparisonRequestDTO);
        await indexDBGlobal.writeJson({ version: gDataVersion, ...data });
      } catch (error) {
        console.warn('Error fetching global comparison data:', error);
      }
    }
    gdata = await indexDBGlobal.ReadGDataIndexDb();
    return gdata;
  } else {
    gdata = await GetGlobalDataService();
    const gDataVersion: GlobalDataVersionDTO = await GetGDataVersion();
    await indexDBGlobal.writeJson({ version: gDataVersion, ...gdata });
    return gdata;
  }

  function compareVersions(
    gdataIndexDBVersion: GlobalDataVersionDTO,
    gDataVersion: GlobalDataVersionDTO,
  ): GlobalDataComparisonResultDTO {
    return {
      usersVersionMatch: gdataIndexDBVersion.usersVersion === gDataVersion.usersVersion,
      containersVersionMatch:
        gdataIndexDBVersion.containersVersion === gDataVersion.containersVersion,
      favouriteIngredientStocksVersionMatch:
        gdataIndexDBVersion.favouriteIngredientStocksVersion ===
        gDataVersion.favouriteIngredientStocksVersion,
      ingredientIngredientTypeVersionMatch:
        gdataIndexDBVersion.ingredientIngredientTypeVersion ===
        gDataVersion.ingredientIngredientTypeVersion,
      ingredientStockVersionMatch:
        gdataIndexDBVersion.ingredientStockVersion ===
        gDataVersion.ingredientStockVersion,
      ingredientTypeVersionMatch:
        gdataIndexDBVersion.ingredientTypeVersion === gDataVersion.ingredientTypeVersion,
      ingredientTypeColorVersionMatch:
        gdataIndexDBVersion.ingredientTypeColorVersion ===
        gDataVersion.ingredientTypeColorVersion,
      ingredientsVersionMatch:
        gdataIndexDBVersion.ingredientsVersion === gDataVersion.ingredientsVersion,
      scoreDefnVersionMatch:
        gdataIndexDBVersion.scoreDefnVersion === gDataVersion.scoreDefnVersion,
      incubationTempsVersionMatch:
        gdataIndexDBVersion.incubationTempsVersion ===
        gDataVersion.incubationTempsVersion,
      setupTempsVersionMatch:
        gdataIndexDBVersion.setupTempsVersion === gDataVersion.setupTempsVersion,
      captureProfileTuningValuesVersionMatch:
        gdataIndexDBVersion.captureProfileTuningValuesVersion ===
        gDataVersion.captureProfileTuningValuesVersion,
      seedListVersionMatch:
        gdataIndexDBVersion.seedListVersion === gDataVersion.seedListVersion,
      screenListVersionMatch:
        gdataIndexDBVersion.screenListVersion === gDataVersion.screenListVersion,
      proteinFormulationListVersionMatch:
        gdataIndexDBVersion.proteinFormulationListVersion ===
        gDataVersion.proteinFormulationListVersion,
      licenseEditionVersionMatch:
        gdataIndexDBVersion.licenseEditionVersion === gDataVersion.licenseEditionVersion,
      allScoresGroupsVersionMatch:
        gdataIndexDBVersion.allScoresGroupsVersion ===
        gDataVersion.allScoresGroupsVersion,
      manualScoreGroupsVersionMatch:
        gdataIndexDBVersion.manualScoreGroupsVersion ===
        gDataVersion.manualScoreGroupsVersion,
    } as GlobalDataComparisonResultDTO;
  }

  function HasAnyValueFalse(comparisonResult: any): boolean {
    return Object.values(comparisonResult).includes(false);
  }
}

export function GetGlobalExpData() {
  return gdata;
}

export function SaveGlobalExpData(data) {
  gdata = data;
}

export function getAllScoreGroups() {
  return GetGlobalExpData()?.allScoresGroups ?? [];
}

export function GetDefaultPlatePadding() {
  return 20;
}

export function GetProteinFormulationName(proteinFormulationId): string {
  const pfList = PFList.filter((pf) => pf.id === proteinFormulationId);
  if (pfList.length > 0) {
    return pfList[0].pfName;
  }

  return '';
}

export function GetProteinFormulation(proteinFormulationId: number) {
  if (!proteinFormulationId) {
    return null;
  }

  const pfList = PFList.filter((pf) => pf.id === proteinFormulationId);
  if (pfList.length > 0) {
    return pfList[0];
  }
  return null;
}

export function GetLicenseEdition() {
  return LicEdition;
}
export function GetPOIDefaultColor() {
  return useCanvasPreference(ECanvasPreferencesKeys.POIColor);
}
export function GetSeedName(seedId) {
  if (!seedId) {
    return '';
  }
  const seedList = SeedList.filter((s) => s.id === seedId);
  if (seedList.length > 0) {
    return seedList[0].name;
  }

  return '';
}

let PlatePadding = GetDefaultPlatePadding();

export function GetPlatePadding() {
  return PlatePadding;
}

export function SetPlatePadding(padding) {
  PlatePadding = padding;
}

export function showSnackBar(text) {
  /**TODO: Replace all showSnackBar function call with showAlertSnackBar**/
  showAlertSnackBar(EAlertType.INFO, ESnackBarPosition.TOP, text);
}

function SnackBarComponent(props: any) {
  React.useEffect(() => {
    if (props.onLoad) {
      props.onLoad();
    }
  }, []);
  return (
    <div
      id={`utils-global-snackbar-${props.text}-wrapper`}
      className="flex w-full h-full items-start justify-start px-[1vw] text-left"
      style={{
        backgroundColor: props.backgroundColor,
        color: props.textColor,
      }}
    >
      <div className="flex items-center gap-[0.5vw] text-left">
        <div
          id={`utils-global-snackbar-${props.icon}-icon`}
          className="flex items-center"
        >
          {props.icon}
        </div>
        <div className="flex flex-col items-start text-left">
          {props.title ? (
            <span className="mb-1 font-semibold text-sm leading-snug text-left">
              {props.title}
            </span>
          ) : null}
          <span
            className={`text-sm leading-snug text-left ${
              props.title ? 'font-normal' : 'font-semibold'
            }`}
          >
            {props.text}
          </span>
        </div>
      </div>
    </div>
  );
}

const ingredientsByIngStockIdDictionary: {
  [key: number]: { ingredient: IngredientDTO; stock: IngredientStockDTO };
} = {};
export const getIngredientsByIngredientStockId = (stockId: number) => {
  /**
   * * This function is used to get the ingredient and stock by the stockId.
   * * First find the ingredient stock by the stockId.
   * * Then find the ingredient by the ingredientId from the ingredient stock.
   * * all using pre-calulated dictionaries.
   * * If the ingredient is not found in the dictionary, then fallback and find it from the ingredients list.
   */
  const _ingredientStock = getIngredientStockByDict(stockId);
  if (!_ingredientStock) {
    return null;
  }

  const _ingredient = getIngredientByIngredientIdDict(_ingredientStock.ingredientId);

  if (_ingredient) {
    return { ingredient: _ingredient, stock: _ingredientStock };
  }

  /**
   * Following code is highly unlikely to be executed.
   * This is just a fallback to find the ingredient and stock by the stockId.
   */
  if (ingredientsByIngStockIdDictionary[stockId]) {
    return ingredientsByIngStockIdDictionary[stockId];
  }

  const ingredientsLookUp = GetIngredients();
  const ingStock = ingredientsLookUp.ingredientStock.find((i) => i.id === stockId);
  const _ing = ingredientsLookUp.ingredients.find((i) => i.id === ingStock.ingredientId);

  const ingStockObj = { ingredient: _ing, stock: ingStock };
  ingredientsByIngStockIdDictionary[stockId] = ingStockObj;

  return ingStockObj;
};
let snackbarRoot: Root | null = null;
let snackbarHideTimeout: ReturnType<typeof setTimeout> | null = null;

const snackbarTimeoutMilliseconds = 5000;

export function showAlertSnackBar(
  alertType: EAlertType,
  position: ESnackBarPosition,
  text: string,
  title?: string,
) {
  const snackbarComponent = document.getElementById('snackbar');
  const textDiv = document.getElementById('snackbar-text');

  if (!snackbarComponent || !textDiv) {
    return;
  }

  if (!snackbarRoot) {
    snackbarRoot = createRoot(textDiv);
  }

  let backgroundColor = '#9c9c9c';
  let textColor = '#ffffff';
  let icon: React.ReactNode = null;

  switch (alertType) {
    case EAlertType.ERROR:
      backgroundColor = 'rgba(255, 209, 209, 1)';
      textColor = 'rgba(164, 0, 0, 1)';
      icon = (
        <FmlxIcon
          id="icon-error"
          name="Error"
          size="sm"
          customColor="rgba(164, 0, 0, 1)"
        />
      );
      break;
    case EAlertType.WARNING:
      backgroundColor = 'rgba(255, 237, 173, 1)';
      textColor = 'rgba(170, 135, 7, 1)';
      icon = (
        <FmlxIcon
          id="icon-warning"
          name="Warning"
          size="sm"
          customColor="rgba(170, 135, 7, 1)"
        />
      );
      break;
    case EAlertType.INFO:
      backgroundColor = 'rgba(204, 225, 253, 1)';
      textColor = 'rgba(0, 89, 204, 1)';
      icon = (
        <FmlxIcon
          id="icon-info"
          name="Info"
          size="sm"
          customColor="rgba(0, 89, 204, 1)"
        />
      );
      break;
    case EAlertType.SUCCESS:
      backgroundColor = 'rgba(195, 228, 197, 1)';
      textColor = 'rgba(51, 117, 53, 1)';
      icon = (
        <FmlxIcon
          id="icon-success"
          name="CheckboxDefault"
          size="sm"
          customColor="rgba(51, 117, 53, 1)"
        />
      );
      break;
  }

  textDiv.style.position = 'fixed';
  textDiv.style.top = '';
  textDiv.style.bottom = '';

  if (position === ESnackBarPosition.TOP) {
    textDiv.style.top = '10px';
  } else {
    textDiv.style.bottom = '10px';
  }

  textDiv.style.backgroundColor = backgroundColor;
  textDiv.style.textAlign = title ? 'left' : 'center';

  if (snackbarHideTimeout) {
    clearTimeout(snackbarHideTimeout);
    snackbarHideTimeout = null;
  }

  snackbarRoot.render(
    <SnackBarComponent
      text={text}
      title={title}
      backgroundColor={backgroundColor}
      textColor={textColor}
      icon={icon}
    />,
  );

  snackbarComponent.classList.remove('show');

  requestAnimationFrame(() => {
    snackbarComponent.classList.add('show');

    snackbarHideTimeout = setTimeout(() => {
      snackbarComponent.classList.remove('show');
      snackbarHideTimeout = null;
    }, snackbarTimeoutMilliseconds);
  });
}

document.addEventListener('visibilitychange', () => {
  if (document.hidden && snackbarHideTimeout) {
    clearTimeout(snackbarHideTimeout);
    snackbarHideTimeout = null;

    const snackbar = document.getElementById('snackbar');
    snackbar?.classList.remove('show');
  }
});

export function GetTreeWidth(): number {
  return 0.4 * window.innerWidth;
}

export function GetHeaderHeight(): number {
  return 0.1 * window.innerHeight;
}

export function GetUnifiedExperimentCanvasWidth(widthMainDiv: number): number {
  // Subtracting 4.7 % from main width for the Main left Route Panel
  // Subtracting 60px for the left mode panel
  return (window.innerWidth - window.innerWidth * 0.02 - 66) * (widthMainDiv / 100) - 20;
}

export function GetUnifiedExperimentCanvasHeight(): number {
  // Subtracting Two time header height (Including Bottom Menu)
  return window.innerHeight - GetHeaderHeight() * 2;
}

const TileSize = {
  1536: [
    [4, 4], // At level 0, assume plate is divided into 4x4 pieces widthxheight
    [16, 16], // At level 1, assume plate is divided into 16x16 pieces (relative to original size)
  ],
  384: [
    [2, 2], // At level 0, assume plate is divided into 4x4 pieces widthxheight
    [4, 4], // At level 1, assume plate is divided into 16x16 pieces (relative to original size)
  ],
  96: [
    [2, 2],
    [4, 4],
  ],
};

export async function getHeaders() {
  const cookieToken = await CacheHelper.get('token');
  const token = 'Bearer ' + cookieToken;
  const machineName = await CacheHelper.get('X-MachineName');

  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = token;
  }
  if (machineName) {
    headers['X-MachineName'] = machineName;
  }

  return headers;
}

export function GetTileSize(key, level, num) {
  if (TileSize[key] === undefined) {
    return null;
  }

  if (TileSize[key][level] === undefined) {
    return null;
  }

  if (TileSize[key][level][num] === undefined) {
    return null;
  }

  return TileSize[key][level][num];
}

let ingredients: GlobalIngredientsDTO;
let ingredientTypeColors: IngredientTypeColorDTO[];
let favouriteStocks: FavoriteIngredientStock[];
let ScoreDefn = null;
let ExpInspections = null;
let WellsLabels = [];
let canvasLabels: PlateCanvasLabels = { rowLabels: [], columnLabels: [] };
let SelectedRoute = null;
export const Routes = {
  Canvas: 'Canvas',
  Well: 'Well',
};
let SeedList = null;
let PFList = null;
let ScreenList = null;
let LicEdition = null;
let PFListDictionary = null;
let SeedListDictionary = null;

export function SetIngredients(ings: GlobalIngredientsDTO) {
  ingredients = ings;
  if (ings) {
    setIngredientByIngredientIdDict();
    setIngredientStockByIngredientStockIdDict();
    setIngredientIngredientTypeByIngIngTypeIdDict();
    setIngredientTypeByIngTypeIdDict();
    setIngredientTypeColorByIngColorIdDict();
  }
}

export function SetFavouriteStocks(stocks: FavoriteIngredientStock[]) {
  favouriteStocks = stocks;
}

export function GetFavouriteStocks() {
  return favouriteStocks;
}

export function GetIngredients() {
  return ingredients;
}

export function getNewFolderType(treenodes, node) {
  if (!node) {
    return;
  }
  const parents = getParents(treenodes, node.id);
  if (!parents) {
    return ETreeNodeTypes.ProjectsFolder;
  }
  const projectOrScreen = parents.find(
    (p) =>
      p.type === ETreeNodeTypes.Project ||
      p.type === ETreeNodeTypes.ProjectFolder ||
      p.type === ETreeNodeTypes.ScreensFolder ||
      p.type === ETreeNodeTypes.Screens ||
      p.type === ETreeNodeTypes.CrystalTrackv4 ||
      p.type === ETreeNodeTypes.Searches,
  );
  if (projectOrScreen) {
    if (
      projectOrScreen.type === ETreeNodeTypes.Project ||
      projectOrScreen.type === ETreeNodeTypes.ProjectFolder
    ) {
      return ETreeNodeTypes.ProjectFolder;
    } else if (projectOrScreen.type === ETreeNodeTypes.CrystalTrackv4) {
      return ETreeNodeTypes.SynchrotronTripv4Folder;
    } else if (projectOrScreen.type === ETreeNodeTypes.Searches) {
      return ETreeNodeTypes.SearchesFolder;
    } else {
      return ETreeNodeTypes.ScreensFolder;
    }
  } else {
    return ETreeNodeTypes.ProjectsFolder;
  }
}
export function SetIngredientTypeColors(ingTypeColors: IngredientTypeColorDTO[]) {
  ingredientTypeColors = ingTypeColors;
}

export function GetIngredientTypeColors(): IngredientTypeColorDTO[] {
  return ingredientTypeColors;
}

export function SetSeedList(seedList) {
  SeedList = seedList;
}

export function SetProteinFormulationList(pfList) {
  PFList = pfList;
}

export function SetLicenseEdition(licEdition) {
  LicEdition = licEdition;
}

export function GetNameProteinLayerUsingPFName() {
  return useCanvasPreference(ECanvasPreferencesKeys.NameProteinLayerUsingPFName);
}

export function GetSeedList() {
  return SeedList;
}

export function GetProteingFormulationList() {
  return PFList;
}

export function SetScreenList(screenList) {
  ScreenList = screenList;
}

export function GetScreenList(): ScreenDTO[] {
  return ScreenList;
}

export function GetScreenById(screenId: number): ScreenDTO {
  const screen: ScreenDTO = ScreenList.find((s) => s.id === screenId);
  return screen;
}

export function SetScoreDefn(scores) {
  ScoreDefn = scores;
}

export function GetScoreDefn() {
  return ScoreDefn;
}
export function SetSelectedRoute(route) {
  SelectedRoute = route;
}

export function GetSelectedRoute() {
  return SelectedRoute;
}

export function SetExpInspections(inspections) {
  ExpInspections = inspections;
}

export function GetExpInspections() {
  return ExpInspections;
}

export function SetWellsLabels(labels) {
  WellsLabels = labels;
}

export function GetWellsLabels() {
  return WellsLabels;
}

export function SetCanvasLabels(labels: PlateCanvasLabels) {
  canvasLabels = labels;
}

export function GetCanvasLabels(): PlateCanvasLabels {
  return canvasLabels;
}

export const SetProteinFormulationDict = (
  proteinFormulations: ProteinFormulationDTO[],
) => {
  const formattedProteinFormulations: { [key: number | null]: ProteinFormulationDTO } =
    proteinFormulations.reduce((protein, pf) => {
      protein[pf.id] = pf;
      return protein;
    }, {} as { [key: number | null]: ProteinFormulationDTO });
  PFListDictionary = formattedProteinFormulations;
};

export const SetSeedDict = (seeds: SeedingDTO[]) => {
  const formattedSeeds: { [key: number | null]: SeedingDTO } = seeds.reduce(
    (seed, sd) => {
      seed[sd.id] = sd;
      return seed;
    },
    {} as { [key: number | null]: SeedingDTO },
  );
  SeedListDictionary = formattedSeeds;
};

export const GetProteinFormulationDict = () => {
  return PFListDictionary;
};

export const GetSeedsDict = () => {
  return SeedListDictionary;
};

function IsPrime(number) {
  if (number <= 1) {
    return false;
  }

  for (let divider = 2; divider <= Math.sqrt(number); divider++) {
    if (number % divider == 0) {
      return false;
    }
  }

  return true;
}
function Sqrt(number, isPertfectSquare) {
  const sqrt = Math.sqrt(number);

  const Intsqrt = parseInt(sqrt.toString());
  isPertfectSquare = false;
  if (number / Intsqrt == Intsqrt) {
    isPertfectSquare = true;
  }

  return { sqrt, isPertfectSquare };
}
function FindPositiveFactors(number) {
  const factors = [];
  factors.push(1);
  for (let divider = 2; divider < Math.sqrt(number); divider++) {
    if (number % divider == 0) {
      factors.push(divider);
      factors.push(number / divider);
    }
  }

  factors.sort((a, b) => a - b);
  return factors;
}
export function FindOptimumMatrixSize(number) {
  if (number <= 0) {
    return { width: 0, height: 0 };
  }

  if (number <= 2) {
    return { width: number - 1, height: 0 };
  }

  if (IsPrime(number)) {
    number++;
  }

  const isPerfectSquare = false;
  const details = Sqrt(number, isPerfectSquare);
  const sqrt = parseInt(details.sqrt.toString());
  if (details.isPertfectSquare) {
    return { width: sqrt - 1, height: sqrt - 1 };
  }

  const factors = FindPositiveFactors(number);
  const size = { width: 0, height: 0 };
  size.height = parseInt(factors[Math.floor(factors.length / 2)].toString());
  size.width = number / size.height;
  size.width -= 1;
  size.height -= 1;
  return size;
}

let IncubTemps = null;

export function SetIncubTemps(incubtemps) {
  IncubTemps = incubtemps;
}

export function GetIncubTemps() {
  return IncubTemps;
}

let SetupTemps = null;

export function SetSetupTemps(setuptemps) {
  SetupTemps = setuptemps;
}

export function GetSetupTemps() {
  return SetupTemps;
}

// //////////////
let _containers: Containers[] = null;

export function SetContainers(containers) {
  _containers = containers;
}

export function GetContainers() {
  return _containers;
}

export function GetContainerFromId(containerId: number) {
  return _containers?.find((container) => container.id === containerId);
}

export function GetContainersForExperimentInfo(
  containerId: number | undefined,
  allowedContainerTypes: number[],
) {
  if (containerId === undefined) {
    return _containers?.filter((container) =>
      allowedContainerTypes.includes(container.type),
    );
  } else {
    return _containers?.filter((container) => {
      if (allowedContainerTypes.includes(container.type)) {
        if (container.enable === false) {
          if (container.id === containerId) {
            return true;
          }
          return false;
        }
        return true;
      }
      return false;
    });
  }
}

let CaptureProfileTuningValues = null;

export function SetCaptureProfileTuningValues(captureProfileTuningValues) {
  CaptureProfileTuningValues = captureProfileTuningValues;
}

export function GetCaptureProfileTuningValues() {
  return CaptureProfileTuningValues;
}

let Users = null;

export function SetUsers(users) {
  Users = users;
}

export function GetUsers() {
  return Users;
}

// //////////////
// / TODO: It should be experiment wise, not overall global
export const DisplayModes = {
  IMAGE: 'IMAGE',
  DESIGN: 'DESIGN',
  SCORE: 'SCORE',
  IMAGESCORE: 'IMAGESCORE',
  WELLIMAGE: 'WELLIMAGE',
  EXPINFO: 'EXPINFO',
  SCREEN: 'SCREEN',
  REPORT: 'REPORT',
  WELLINGREDIENT: 'WELLINGREDIENT',
};
export const ExperimentRoutes = {
  CANVAS: 'CANVAS',
  DROP: 'DROP',
  EXPINFO: 'EXPINFO',
};
export function getFolderType(type: ETreeNodeTypes): ETreeNodeTypes {
  if (type === ETreeNodeTypes.Projects || type === ETreeNodeTypes.ProjectsFolder) {
    return ETreeNodeTypes.ProjectsFolder;
  }
  if (type === ETreeNodeTypes.Project || type === ETreeNodeTypes.ProjectFolder) {
    return ETreeNodeTypes.ProjectFolder;
  }
}

let ExpRoute: string = ExperimentRoutes.CANVAS;

export function SetExpRoute(route: string) {
  ExpRoute = route;
}

export function GetExpRoute(): string {
  return ExpRoute;
}

let DisplayMode: string = DisplayModes.IMAGE;
let IsImagingActive = false;

export function SetDisplayMode(mode: string) {
  DisplayMode = mode;
}

export function GetDisplayMode(): string {
  return DisplayMode;
}

export function SetImagingStatus(imgStatus) {
  IsImagingActive = imgStatus;
}

export function GetImagingStatus() {
  return IsImagingActive;
}

let IsNavigatorActive = false;
export function SetNavigatorStatus(navStatus: boolean) {
  IsNavigatorActive = navStatus;
}

export function GetNavigatorStatus(): boolean {
  return IsNavigatorActive;
}

const precision = 100;
// https://stackoverflow.com/questions/11832914/round-to-at-most-2-decimal-places-only-if-necessary

export function Round(num: number): number {
  return Math.round((num + Number.EPSILON) * precision) / precision;
}

let RectCounter = 0;

export function SetRectCounter(c: number) {
  RectCounter = c;
}

export function GetRectCounter(): number {
  return RectCounter;
}

export function GetUIRect({
  x,
  y,
  w,
  h,
  fillColor,
  bColor,
  strokeWidth,
  listening,
  opacity,
  rectDesign = undefined,
}: RMRectangle): React.ReactNode {
  const rect = (
    <Rect
      key={RectCounter++}
      x={x}
      y={y}
      width={w}
      height={h}
      fill={fillColor}
      stroke={bColor}
      strokeWidth={strokeWidth || 0.5}
      shadowBlur={0}
      listening={listening || false}
      opacity={opacity}
    />
  );
  return rectDesign ? <>{[rect, rectDesign]}</> : rect;
}
export function GetUIText({ x, y, w, h, size, name, color }: TextProps): React.ReactNode {
  return (
    <Text
      x={x}
      y={y}
      width={w}
      height={h}
      fontSize={size}
      textAnchor="middle"
      text={name}
      align="center"
      verticalAlign="middle"
      wrap="none"
      ellipsis={true}
      fill={color ? color : 'black'}
    />
  );
}

export function GetDetailUIText({
  x,
  y,
  w,
  h,
  size,
  name,
  stroke,
  align,
  fill,
  strokeWidth,
}: DetailTextProps): React.ReactNode {
  return (
    <Text
      x={x}
      y={y}
      width={w}
      height={h}
      fontSize={size}
      textAnchor="middle"
      verticalAlign="middle"
      text={name}
      stroke={stroke}
      strokeWidth={strokeWidth}
      fill={fill}
      align={align || 'center'}
    />
  );
}
export default TileSize;

// this is getting called twice as plate.width changes after cmoposite is loaded. ok for now, as we want to let platerenderer getting called
// as early as possible, and not wait for actual width before calling platerenderer
export function InitWellRects(plate, rows, cols) {
  // wellRects are bounding rects for each Well. All ingredients rects (Water etc) are contained inside this

  plate.wellRects = [];
  plate.wellRectsPadded = [];

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const x = plate.startX + col * (plate.width / cols);
      const y = plate.startY + row * (plate.height / rows);
      const w = plate.width / cols;
      const h = plate.height / rows;
      const actualWellRectangle: RMRectangle = {
        x,
        y,
        w,
        h,
      };
      const pad = actualWellRectangle.w * 0.05;
      const wellRectangle: RMRectangle = {
        x: actualWellRectangle.x + pad,
        y: actualWellRectangle.y + pad,
        w: actualWellRectangle.w - pad * 2,
        h: actualWellRectangle.h - pad * 2,
      };
      plate.wellRectsPadded.push(wellRectangle);
      plate.wellRects.push(actualWellRectangle);
    }
  }
}

export function Copy(o) {
  let v, key;
  const out = Array.isArray(o) ? [] : {};
  for (key in o) {
    v = o[key];
    out[key] = typeof v === 'object' && v !== null ? Copy(v) : v;
  }
  return out;
}
export function checkIsValidInput(value, allowNegatives?: boolean): boolean {
  if (isNullOrUndefined(allowNegatives) || !allowNegatives) {
    const regex = new RegExp(/^([0-9]*\.?[0-9]{0,4}|\.[0-9]{0,4})$/);

    return regex.test(value);
  }
  const regex = new RegExp(/^-?([0-9]*\.?[0-9]{0,4}|\.[0-9]{0,4})$/);
  return regex.test(value);
}

export function setRoundedValue(concValue, setValue) {
  const splitValue = concValue.split('.');
  const secondElement = splitValue[1];
  let returnValue;
  if (!isNullOrUndefined(secondElement) && secondElement.length > 0) {
    const floatedVal = parseFloat(concValue);
    if (secondElement == '0') {
      returnValue = `${floatedVal}.0`;
    } else if (secondElement == '00') {
      returnValue = `${floatedVal}.00`;
    } else if (secondElement == '000' || secondElement == '0000') {
      returnValue = `${floatedVal}.000`;
    } else {
      const roundedValue = Math.floor(floatedVal * 1000) / 1000;
      const finalValue = roundedValue.toString();
      if (secondElement.length <= 3 && secondElement[secondElement.length - 1] == '0') {
        returnValue = finalValue + '0';
      } else {
        returnValue = finalValue;
      }
    }
  } else {
    returnValue = concValue;
  }
  setValue(returnValue);
  return returnValue;
}
export const LayerType = Object.freeze({
  Grid: 1,
  Protein: 2,
  ConditionList: 3,
  Random: 4,
  Screen: 5,
  Seed: 6,
  Fragment: 7,
});
export function GetRelativePointerPosition(node) {
  if (!node) {
    return { x: 0, y: 0 };
  }
  const transform = node.getAbsoluteTransform().copy();
  // to detect relative position we need to invert transform
  transform.invert();

  // get pointer (say mouse or touch) position
  const stage = node.getStage();
  if (!stage) {
    return { x: 0, y: 0 };
  }
  const pos = stage.getPointerPosition();

  // now we can find relative point
  return transform.point(pos);
}

export function getImagingTaskSourceLabel(plateState) {
  if (plateState === EImagingTaskSource.RMScheduling) {
    return 'Scheduled';
  }
  if (plateState === EImagingTaskSource.RMManual) {
    return 'RM Manual';
  }
  if (plateState === EImagingTaskSource.RMImageNow) {
    return 'RM ImagePlate';
  }
  if (plateState === EImagingTaskSource.RMTemporary) {
    return 'RM Temporary';
  }
  if (plateState === EImagingTaskSource.RIManual) {
    return 'RI Manual';
  }
  if (plateState === EImagingTaskSource.RIImageNow) {
    return 'RI ImagePlate';
  }
  if (plateState === EImagingTaskSource.CrystalFarmManual) {
    return 'CrystalFarm Manual';
  }
  if (plateState === EImagingTaskSource.AutoInspeXManual) {
    return 'AutoInspeX Manual';
  }
  if (plateState === EImagingTaskSource.RhombixManual) {
    return 'Rhombix Manual';
  }
  if (plateState === EImagingTaskSource.RoboMicroscopeManual) {
    return 'RoboMicroscope Manual';
  }
  if (plateState === EImagingTaskSource.TriTekManual) {
    return 'TriTek Manual';
  }
  if (plateState === EImagingTaskSource.DSIManual) {
    return 'DSI Manual';
  }
  return '';
}

export function getImagingTaskStateLabel(plateState) {
  if (plateState === EImagingTaskState.NotCompleted) {
    return 'Not Completed';
  }
  if (plateState === EImagingTaskState.Skipped) {
    return 'Skipped';
  }
  if (plateState === EImagingTaskState.Pending) {
    return 'Pending';
  }
  if (plateState === EImagingTaskState.Queued) {
    return 'Queued';
  }
  if (plateState === EImagingTaskState.Imaging) {
    return 'Imaging';
  }
  if (plateState === EImagingTaskState.Completed) {
    return 'Completed';
  }
  if (plateState === EImagingTaskState.Cancelled) {
    return 'Disabled';
  }
  if (plateState === EImagingTaskState.ForceComplete) {
    return 'Force Complete';
  }
  return '';
}

export function getFormatedDate(unformated) {
  if (unformated == null || unformated === undefined || unformated.length === 0) {
    return null;
  }
  const dateTime = unformated.split('T');
  let date = dateTime[0];
  let time = dateTime[1];

  date = date.replace(/-/g, '/');
  time = time.split(':');
  const dayNight = time[0] >= 12 ? 'PM' : 'AM';
  let hour = time[0] === '12' ? '12' : `${time[0] % 12}`;
  hour = parseInt(hour) < 10 ? `0${hour}` : `${hour}`;
  time = `${hour}:${time[1]} ${dayNight}`;
  return `${date}\n${time}`;
}

export const getUniqArrBy = (props = [], arrInp = [{}]) => {
  const objKey = arrInp.reduce((res, item) => {
    const valStr = props.reduce((res, prop) => `${res}${item[prop]}`, '');
    return { ...res, [valStr]: item };
  }, {});
  return Object.keys(objKey).map((item) => objKey[item]);
};

export function isNullOrUndefined(val) {
  return val == null || val == undefined;
}
function GetColorBrightness(r, g, b) {
  // See: http://www.w3.org/TR/AERT#color-contrast
  // See: http://en.wikipedia.org/wiki/YIQ
  return 0.299 * r + 0.587 * g + 0.114 * b;
}
function GetBrightness(r, g, b) {
  return GetColorBrightness(r / 255, g / 255, b / 255);
}

export function GetTextColor(backgroundColor) {
  const [red, blue, green] = backgroundColor.split(',');
  const bgBright = GetBrightness(red, blue, green);
  const whiteBright = GetBrightness(255, 255, 255);
  const blackBright = GetBrightness(0, 0, 0);
  const whiteDiff = Math.abs(bgBright - whiteBright);
  const blackDiff = Math.abs(bgBright - blackBright);
  return whiteDiff > blackDiff ? EColors.White : EColors.Black;
}

export function invertColor(hexColor) {
  let color = hexColor;
  color = color.substring(1); // remove #
  color = parseInt(color, 16); // convert to integer
  color = 0xffffff ^ color; // invert three bytes
  color = color.toString(16); // convert to hex
  color = ('000000' + color).slice(-6); // pad with leading zeros
  color = '#' + color; // prepend #
  return color;
}

export function getIPScoreSymbol(ipscore) {
  if (ipscore === EAutoScoreImageClass.NegativeHit) {
    return '-';
  }
  if (ipscore === EAutoScoreImageClass.PositiveHit) {
    return '+';
  }
  return '';
}

export function compareWithScoreGroupId(a, b) {
  const scoreA = a.wellDropScoreGroupId;
  const scoreB = b.wellDropScoreGroupId;

  let comparison = 0;
  if (scoreA > scoreB) {
    comparison = 1;
  } else if (scoreA < scoreB) {
    comparison = -1;
  }
  return comparison;
}
export function compareWithIngredientName(
  a: IngredientIngredientType,
  b: IngredientIngredientType,
) {
  const ingAName = getIngNameByIngId(a.ingredientId);
  const ingBName = getIngNameByIngId(b.ingredientId);

  return compareWithName(ingAName, ingBName);
}

export function applyScoring(allInspectionsScores, screWell) {
  const allScoreIndex = allInspectionsScores.findIndex(
    (wds) =>
      wds.wellNumber === screWell.wellNumber &&
      wds.wellDropScoreGroupId === screWell.wellDropScoreGroupId &&
      wds.scoreBatchId === screWell.imageBatchId &&
      wds.dropNumber === screWell.dropNumber,
  );
  let scoreObj = [];
  if (screWell.wellDropScoreGroupId === 11 && !screWell.isDeleteScore) {
    const inspections = GetExpInspections();
    const dropInspections = [];
    inspections.forEach((inspection) => {
      inspection.groupedInspections.forEach((gi) =>
        dropInspections.push(gi.imageBatchId),
      );
    });
    const uniqueInspections = [...dropInspections];
    uniqueInspections.forEach((di) =>
      scoreObj.push({
        wellNumber: screWell.wellNumber,
        wellDropScoreId: screWell.wellDropScoreId,
        wellDropScoreGroupId: screWell.wellDropScoreGroupId,
        isAutoScore: false,
        scoreBatchId: di,
        dropNumber: screWell.dropNumber,
        scoreType: EScoreType.Manual,
      }),
    );
  } else {
    scoreObj = [
      {
        wellNumber: screWell.wellNumber,
        wellDropScoreId: screWell.wellDropScoreId,
        wellDropScoreGroupId: screWell.wellDropScoreGroupId,
        isAutoScore: false,
        scoreBatchId: screWell.imageBatchId,
        dropNumber: screWell.dropNumber,
        scoreType: EScoreType.Manual,
      },
    ];
  }
  if (screWell.isDeleteScore && screWell.wellDropScoreGroupId === 11) {
    allInspectionsScores = allInspectionsScores.filter(
      (scores) => scores.wellDropScoreGroupId !== 11,
    );
  } else if (screWell.isDeleteScore && allScoreIndex !== -1) {
    allInspectionsScores.splice(allScoreIndex, 1);
  } else if (!screWell.isDeleteScore && screWell.wellDropScoreGroupId === 11) {
    allInspectionsScores = [...allInspectionsScores, ...scoreObj];
  } else if (allScoreIndex === -1) {
    // If selected well have scores from other score groups but not from the scoregroup which we have scored so it will be added in that well scores
    allInspectionsScores.push(scoreObj[0]);
  } else if (allScoreIndex !== -1) {
    allInspectionsScores.splice(allScoreIndex, 1, scoreObj[0]);
  }
  return allInspectionsScores;
}

export function getIngredientName(ingIngTypeId) {
  const globalIngredients = GetIngredients();

  const ingIngType: IngredientIngredientType =
    globalIngredients.ingredientIngredientType.find((ing) => ing.id == ingIngTypeId);

  const ingredient = globalIngredients.ingredients.find(
    (ing) => ing.id === ingIngType.ingredientId,
  );

  return ingredient.longName;
}

// This function is used to set the ingredient stock by ingredient stock id, maintains dictionary of list[ingredientstockid] = ingredientstock
const ingredientsStockByIdDict = {};
export function setIngredientStockByIngredientStockIdDict() {
  const globalIngredients = GetIngredients();

  if (!globalIngredients) {
    return;
  }

  globalIngredients.ingredientStock.forEach(
    (ingStock) => (ingredientsStockByIdDict[ingStock.id] = ingStock),
  );
}

export function getIngredientStockByDict(stockId) {
  if (ingredientsStockByIdDict[stockId]) {
    return ingredientsStockByIdDict[stockId];
  }

  const globalIngredients = GetIngredients();
  return globalIngredients?.ingredientStock.find((ingStock) => ingStock.id === stockId);
}

// This function is used to set the ingredient by ingredient id, maintains dictionary of ingredient id
const ingredientsByIdDict = {};
export function setIngredientByIngredientIdDict() {
  const globalIngredients = GetIngredients();

  if (!globalIngredients) {
    return;
  }

  globalIngredients.ingredients.forEach((ing) => (ingredientsByIdDict[ing.id] = ing));
}

export function getIngredientByIngredientIdDict(ingId) {
  if (!ingId) {
    return null;
  }

  if (ingredientsByIdDict[ingId]) {
    return ingredientsByIdDict[ingId];
  }

  const globalIngredients = GetIngredients();
  const ingredient = globalIngredients.ingredients.find((ing) => ing.id === ingId);

  return ingredient;
}

/**
 * * This function is used to set the ingredientIngredient type by ingredientIngredient type id, maintains dictionary of ingredientIngredientTypeId
 */
const ingredientIngredientTypeByIngIngTypeId = {};
export function setIngredientIngredientTypeByIngIngTypeIdDict() {
  const globalIngredients = GetIngredients();

  if (!globalIngredients) {
    return;
  }

  globalIngredients.ingredientIngredientType.forEach(
    (ingIngType) => (ingredientIngredientTypeByIngIngTypeId[ingIngType.id] = ingIngType),
  );
}

/**
 * * This function is used to get the ingredientIngredient type by ingredientIngredient type id from maintained dictionary of ingredientIngredientTypeId
 */
export function getIngredientIngredientTypeByIngIngTypeIdDict(ingIngTypeId) {
  if (!ingIngTypeId) {
    return null;
  }

  if (ingredientIngredientTypeByIngIngTypeId[ingIngTypeId]) {
    return ingredientIngredientTypeByIngIngTypeId[ingIngTypeId];
  }

  // fallback
  const _ings = GetIngredients();
  return _ings.ingredientIngredientType.find(
    (ingIngType) => ingIngType.id === ingIngTypeId,
  );
}

const ingredientTypeByIngTypeId = {};
/**
 * This function is used to set the ingredient type by ingredient type id, maintains dictionary of ingredientTypeId
 */
export function setIngredientTypeByIngTypeIdDict() {
  const globalIngredients = GetIngredients();

  if (!globalIngredients) {
    return;
  }

  globalIngredients.ingredientType.forEach(
    (ingType) => (ingredientTypeByIngTypeId[ingType.id] = ingType),
  );
}

/**
 * This function is used to get the ingredient type by ingredient type id from maintained dictionary of ingredientTypeId
 */
export function getIngredientTypeByIngTypeIdDict(ingTypeId) {
  if (!ingTypeId) {
    return null;
  }

  if (ingredientTypeByIngTypeId[ingTypeId]) {
    return ingredientTypeByIngTypeId[ingTypeId];
  }

  // fallback
  const _ings = GetIngredients();
  return _ings.ingredientType.find((ingTyp) => ingTyp.id == ingTypeId);
}

/**
 * Colors dictionary of ingredientTypeColorId
 */
const ingredientTypeColorByIngColorId = {};
export function setIngredientTypeColorByIngColorIdDict() {
  const globalIngredients = GetIngredients();

  if (!globalIngredients) {
    return;
  }

  globalIngredients.ingredientTypeColor.forEach(
    (ingColor) => (ingredientTypeColorByIngColorId[ingColor.id] = ingColor),
  );
}

export function getIngredientTypeColorByIngColorIdDict(ingColorId) {
  if (!ingColorId) {
    return null;
  }

  if (ingredientTypeColorByIngColorId[ingColorId]) {
    return ingredientTypeColorByIngColorId[ingColorId];
  }

  // fallback
  const _ings = GetIngredients();
  return _ings.ingredientTypeColor.find((ingColor) => ingColor.id === ingColorId);
}

export function getIngNameByIngId(ingId) {
  const ingredient = getIngredientByIngredientIdDict(ingId);

  return ingredient?.longName;
}

export function getIngShortNameByIngId(ingId) {
  const globalIngredients = GetIngredients();

  const ingredient = globalIngredients.ingredients.find((ing) => ing.id === ingId);

  return ingredient.shortName;
}

export function getStockConcVendor(stockData: IngredientStockDTO, ispHDisplay: boolean) {
  return `${stockData.stockConcentration} ${stockData?.concentrationUnits}, ${
    isNullOrUndefined(stockData?.vendor?.name)
      ? ELayersDefaultInput.VENDORNA
      : stockData?.vendor?.name
  }${stockData.isBuffer && ispHDisplay ? `, pH ${stockData.stockPh}` : ''} `;
}

export function ingredientTypeName(ingIngTypeId) {
  const globalIngredients = GetIngredients();

  const ingIngType: IngredientIngredientType =
    globalIngredients.ingredientIngredientType.find((ing) => ing.id == ingIngTypeId);

  const ingType = globalIngredients.ingredientType.find(
    (ingType) => ingType.id === ingIngType.id,
  );
  return ingType.name;
}

export function ingredientTypeByTypeId(ingIngTypeId) {
  const globalIngredients = GetIngredients();

  const ingIngType: IngredientIngredientType =
    globalIngredients.ingredientIngredientType.find((ing) => ing.id == ingIngTypeId);

  const ingType = globalIngredients.ingredientType.find(
    (ingType) => ingType.id === ingIngType.ingredientTypeId,
  );
  return ingType;
}

export function getIngredientTypeName(ingIngTypeId) {
  const globalIngredients = GetIngredients();

  const ingIngType: IngredientIngredientType =
    globalIngredients.ingredientIngredientType.find((ing) => ing.id == ingIngTypeId);

  const ingType = globalIngredients.ingredientType.find(
    (ing) => ing.id === ingIngType.ingredientTypeId,
  );

  return ingType.name;
}

export function getIngredientStock(stockId) {
  return getIngredientStockByDict(stockId);
}

export function getAllIngredientStocks(ingIngTypeId): IngredientStockDTO[] {
  const ingIngType = getIngIngType(ingIngTypeId);
  const ingType = getIngredientType(ingIngType.ingredientTypeId);
  const stocks = GetIngredients().ingredientStock.filter(
    (ingStock) =>
      ingStock.ingredientId === ingIngType.ingredientId &&
      ingStock.isBuffer === ingType.isBuffer,
  );
  return isNullOrUndefined(stocks) ? [] : stocks;
}

export function padZeros(n, width) {
  const z = '0';
  n = n + '';
  return n.length >= width ? n : new Array(width - n.length + 1).join(z) + n;
}

export function roundStringValue(val) {
  return parseFloat(val).toFixed(3);
}

export function isBufferIngredient(ingredientId) {
  if (ingredientId == -1) {
    return false;
  }
  const ingredientType = ingredients.ingredientIngredientType.find(
    (ing) => ing.ingredientId === ingredientId,
  );
  if (isNullOrUndefined(ingredientType)) {
    return false;
  }
  const ingType = getIngredientType(ingredientType.ingredientTypeId);
  return ingType.isBuffer;
}

export function getIngIdStockNumber(ingId, stockNumber) {
  return `(${padZeros(ingId, 5)} - ${padZeros(stockNumber, 3)})`;
}

export function getIngredientColor(ingIngTypeId) {
  const ingTypeColor = getIngredientTypeColorByIngIngTypeId(ingIngTypeId);
  return ingTypeColor.color;
}
export function getIngredientTypeColorGroup(ingIngTypeId): number {
  const ingTypeColor = getIngredientTypeColorByIngIngTypeId(ingIngTypeId);
  return ingTypeColor.groupId;
}

export function getIngTypeColorByIngIngTypeId(ingIngTypeId): IngredientTypeColorDTO {
  const ingTypeColor = getIngredientTypeColorByIngIngTypeId(ingIngTypeId);
  return ingTypeColor;
}

export function compareWithStocksPh(a, b) {
  const phA = a.stockPh;
  const phB = b.stockPh;

  let comparison = 0;
  if (phA > phB) {
    comparison = 1;
  } else if (phA < phB) {
    comparison = -1;
  }
  return comparison;
}
export function compareWithStockConcen(
  a: IngredientIngredientType,
  b: IngredientIngredientType,
) {
  let comparison = 0;

  const ingA = getIngredient(a.ingredientId);
  const ingB = getIngredient(b.ingredientId);
  const stockA = getIngredientStock(ingA.defaultNonBufferIngredientStockId);
  const stockB = getIngredientStock(ingB.defaultNonBufferIngredientStockId);
  if (isNullOrUndefined(stockA)) {
    return -1;
  }
  if (isNullOrUndefined(stockB)) {
    return 0;
  }

  const concA = stockA.stockConcentration;
  const concB = stockB.stockConcentration;

  if (concA > concB) {
    comparison = 1;
  } else if (concA < concB) {
    comparison = -1;
  }
  return comparison;
}
export function compareStockByStockConcen(
  stockA: IngredientStockDTO,
  stockB: IngredientStockDTO,
) {
  let comparison = 0;
  if (isNullOrUndefined(stockA)) {
    return -1;
  }
  if (isNullOrUndefined(stockB)) {
    return 0;
  }

  const concA = stockA.stockConcentration;
  const concB = stockB.stockConcentration;

  if (concA > concB) {
    comparison = 1;
  } else if (concA < concB) {
    comparison = -1;
  }
  return comparison;
}

export function compareWithStockPh(
  a: IngredientIngredientType,
  b: IngredientIngredientType,
) {
  const ingredientA: IngredientDTO = getIngredient(a.ingredientId);
  const ingredientB: IngredientDTO = getIngredient(b.ingredientId);
  const stockA = getIngredientStock(ingredientA.defaultBufferIngredientStockId);
  const stockB = getIngredientStock(ingredientB.defaultBufferIngredientStockId);

  if (isNullOrUndefined(stockA) || isNullOrUndefined(stockB)) {
    return 0;
  }

  const phA = stockA.stockPh;
  const phB = stockB.stockPh;
  const concA = stockA.stockConcentration;
  const concB = stockB.stockConcentration;

  if (concA > concB) {
    return 1;
  } else if (concA < concB) {
    return -1;
  }

  if (phA > phB) {
    return 1;
  } else if (phA < phB) {
    return -1;
  } else {
    return 0;
  }
}

export function compareStockByStockPh(
  stockA: IngredientStockDTO,
  stockB: IngredientStockDTO,
) {
  if (isNullOrUndefined(stockA) || isNullOrUndefined(stockB)) {
    return 0;
  }

  const phA = stockA.stockPh;
  const phB = stockB.stockPh;
  const concA = stockA.stockConcentration;
  const concB = stockB.stockConcentration;

  if (concA > concB) {
    return 1;
  } else if (concA < concB) {
    return -1;
  }

  if (phA > phB) {
    return 1;
  } else if (phA < phB) {
    return -1;
  } else {
    return 0;
  }
}
export function GetWellRowNumber(
  wellRow: WellIngDTO,
  containerNumColumns: number,
): number {
  let wellRowNumber = wellRow.wellNumber / containerNumColumns;
  if (wellRow.wellNumber % containerNumColumns > 0) {
    wellRowNumber++;
  }

  return wellRowNumber;
}

export function getVaryAlongName(varyAlong) {
  let varyAlongName = 'None';
  switch (varyAlong) {
    case EGridVaryAlong.ROW:
      varyAlongName = 'Row';
      break;
    case EGridVaryAlong.COLUMN:
      varyAlongName = 'Column';
      break;
    case EGridVaryAlong.WELLNUMBER:
      varyAlongName = 'Well Number';
      break;
    case EGridVaryAlong.LINKEDLAYER:
      varyAlongName = 'Linked Layer';
      break;
    case EGridVaryAlong.CUSTOM:
      varyAlongName = 'Custom';
      break;
    default:
      break;
  }
  return varyAlongName;
}

export const openNode = (route, target, nodeId, openedWindows, setOpenedWindows) => {
  const openedWindowsIds = JSON.parse(localStorage.getItem('openedNodesIds'));
  const idsList = !openedWindowsIds ? [] : openedWindowsIds;
  const checkWindow = openedWindows.find((win) => win?.location?.href?.includes(route));
  if (!checkWindow || !idsList || !idsList.find((win) => win == nodeId)) {
    const newOpenedWindows = [...openedWindows].filter(
      (win) => !win?.location?.href?.includes(route),
    );
    const curWindow = window.open(route, target);
    newOpenedWindows.push(curWindow);
    setOpenedWindows(newOpenedWindows);
  } else if (idsList.find((win) => win == nodeId)) {
    checkWindow.focus();
  }
};

export function getIngIngType(ingIngId) {
  return getIngredientIngredientTypeByIngIngTypeIdDict(ingIngId);
}

export function isBufferIngredientById(ingTypeId) {
  const ingType = GetIngredients().ingredientType.find(
    (ingType) => ingType.id === ingTypeId,
  );

  if (isNullOrUndefined(ingType)) {
    return false;
  }

  return ingType.isBuffer;
}

export function getIngredientType(ingTypeId: number): IngredientTypeDTO {
  return getIngredientTypeByIngTypeIdDict(ingTypeId);
}

export function getIngredientTypeColor(ingColorId) {
  return getIngredientTypeColorByIngColorIdDict(ingColorId);
}
export function getIngredientTypeColorByIngIngTypeId(
  ingIngTypeId,
): IngredientTypeColorDTO {
  const _ingIngType = getIngredientIngredientTypeByIngIngTypeIdDict(ingIngTypeId);
  const _ingType = getIngredientTypeByIngTypeIdDict(_ingIngType.ingredientTypeId);

  return getIngredientTypeColorByIngColorIdDict(_ingType.ingredientTypeColorId);
}
export function getActiveObjProperty(obj) {
  for (const a in obj) {
    if (obj[a]) {
      return a;
    }
  }
  return null;
}

export function compareWithName(a: string, b: string) {
  const lowerA = a.toLowerCase();
  const lowerB = b.toLowerCase();

  let comparison = 0;
  if (lowerA > lowerB) {
    comparison = 1;
  } else if (lowerA < lowerB) {
    comparison = -1;
  }
  return comparison;
}
export async function getUserId() {
  let userId = undefined;
  const token = await getUserToken();
  if (!token || token === 'undefined' || token === 'null') {
    return userId;
  }
  const decodedToken = jwtDecode(token);
  if (!isNullOrUndefined(decodedToken)) {
    userId = decodedToken['UserId'];
  }

  return userId;
}

export async function getUserToken() {
  const token = await CacheHelper.get('token');
  return token;
}
export function getCurrentUser() {
  return JSON.parse(localStorage.getItem('user'));
}

export function isAdministrator() {
  const user: UserDTO = getCurrentUser();

  return user?.groups?.some((g) => g.name === 'Administrators');
}

export function getSubGridNumber(docData, layer) {
  const linkedLayers = docData.layers.filter(
    (lyr) =>
      lyr.type == LayerTypeDTO.Grid && lyr.layerDetail.gridId == layer.layerDetail.gridId,
  );
  let subGridNumber = 2;
  for (let i = 0; i < linkedLayers.length; i++) {
    if (linkedLayers[i].layerDetail.subGridNumber >= subGridNumber) {
      subGridNumber = linkedLayers[i].layerDetail.subGridNumber + 1;
    }
  }
  return subGridNumber;
}

export function getLinkedLayers(
  layers: LayerDTO[],
  selectedLayer: LayerDTO,
  excludeParent = false,
) {
  if (isNullOrUndefined(layers) || layers.length === 0) {
    return null;
  }
  const sortedLayers = [...layers].sort((a, b) => compareWithName(a.name, b.name));
  const linkedLayers = sortedLayers.filter(
    (layer) =>
      layer.type == LayerTypeDTO.Grid &&
      !isNullOrUndefined(layer.layerDetail as GridLayerDTO) &&
      !isNullOrUndefined(selectedLayer.layerDetail as GridLayerDTO) &&
      (layer.layerDetail as GridLayerDTO).gridId ==
        (selectedLayer.layerDetail as GridLayerDTO).gridId &&
      (!excludeParent || layer.id != selectedLayer.id),
  );
  return linkedLayers;
}

export function copyArray(source, sStart, desitination, dStart, length) {
  if (desitination.length > source.length) {
    return;
  }
  let j = dStart;
  for (let i = sStart; i < length; i++) {
    desitination[j] = source[i];
    j++;
  }
}

export function setConcentrationLayersVolume(layers: LayerDTO[], vol: number) {
  const concLayers = layers.filter((layer) => layer.volumeBasedAddition == false);
  concLayers.forEach((layerData) => {
    switch (layerData.type) {
      case LayerTypeDTO.Grid:
        getLinkedLayerGridDto(layers, layerData.layerDetail).startWellVolume = vol;
        break;
      case LayerTypeDTO.Screen:
      case LayerTypeDTO.Protein:
        layerData.volume = vol;
        break;
      case LayerTypeDTO.Random:
      case LayerTypeDTO.ConditionList:
        const customLayer = layerData.layerDetail as CustomLayerDTO;
        customLayer.wellVolume = vol;
        break;
      default:
        break;
    }
    layerData.layerState = LayerStateDTO.Modified;
    layerData.dirty = true;
  });
}

export function deleteByName(data, itemtoRemove, itemtoAdd = null) {
  const deleteIndex = data.findIndex((item) => {
    return item == null ? false : item.id == itemtoRemove.id;
  });

  data.splice(deleteIndex, 1, itemtoAdd);

  return deleteIndex;
}

export function deleteWizardScreen(data, itemtoRemove, itemtoAdd = null) {
  const deleteIndex = data.findIndex((item) => {
    return item == null ? false : item.id == itemtoRemove.id;
  });
  if (itemtoAdd != null) {
    data.splice(deleteIndex, 1, itemtoAdd);
  } else {
    data.splice(deleteIndex, 1);
  }
  return deleteIndex;
}
export function getWizardDefaultPlateType() {
  const defaultPlate = new PlateTypeDTO();
  defaultPlate.id = -1;
  defaultPlate.name = 'Select Plate Type';
  defaultPlate.enabledDropNumbers = [];
  defaultPlate.dropVolume = 0;
  defaultPlate.totalDrops = 0;
  defaultPlate.volume = 0;
  return defaultPlate;
}

export function getParents(nodes, pTreeNodeId) {
  let array;
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].id === pTreeNodeId) {
      return [nodes[i]];
    }
    if (nodes[i].children) {
      const child = getParents(nodes[i].children, pTreeNodeId);
      if (child !== undefined && child.length !== 0) {
        child.push(nodes[i]);
        array = child;
      }
    }
  }
  return array;
}

export function getParentPath(parents) {
  let url = '';
  for (const parent of parents) {
    if (url === '') {
      url = parent.name;
    } else {
      url = parent.name + '\\' + url;
    }
  }
  return url;
}

export function getIngredientByIngIngTypeId(ingIngId) {
  const ingIngType = ingredients.ingredientIngredientType.find(
    (ingType) => ingType.id === ingIngId,
  );
  if (isNullOrUndefined(ingIngType)) {
    return null;
  }
  const ingredient = ingredients.ingredients.find(
    (ing) => ing.id === ingIngType.ingredientId,
  );
  return ingredient;
}
export function getScoreText(wellDropScore, score) {
  //'MARCO should be comming from system property Name: Score.AutoScoreGroupPropertyValue
  if (
    wellDropScore.autoScoreType === EAutoScoreType.FRAP ||
    score?.scoreGroupName.toUpperCase().includes('MARCO')
  ) {
    if (isNullOrUndefined(wellDropScore.autoScoreValue)) {
      return '';
    } else {
      return wellDropScore.autoScoreValue.toFixed(2);
    }
  } else if (wellDropScore.autoScoreType === EAutoScoreType.IP) {
    return getIPScoreSymbol(wellDropScore.ipScoreValue);
  } else {
    return score.hotKey;
  }
}

export function isCommonWellDetails(
  currentWell: WellDetailsStates,
  selectedWell: WellDetailsStates,
) {
  if (isNullOrUndefined(currentWell) || isNullOrUndefined(selectedWell)) {
    return false;
  }
  return (
    currentWell.cpID === selectedWell.cpID &&
    currentWell.dropNumber === selectedWell.dropNumber &&
    currentWell.plateID === selectedWell.plateID &&
    currentWell.wellNum === selectedWell.wellNum &&
    currentWell.regionName === selectedWell.regionName &&
    currentWell.inspectionSet.id === selectedWell.inspectionSet.id
  );
}

export function UpdateArray(arr, element, isAdd, setdisable = null) {
  if (isAdd) {
    if (!arr.includes(element, 0)) {
      arr.push(element);
      setdisable && setdisable(arr.length < 1);
    }
  } else {
    const index = arr.indexOf(element);
    if (index > -1) {
      arr.splice(index, 1);
      setdisable && setdisable(arr.length < 1);
    }
  }
}

export function downloadFilefromUrl(url: string, fileName: string) {
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  // Clean up and remove the link
  a.parentNode.removeChild(a);
}
export const getContainerName = (id) => {
  const _containers: Containers[] = GetContainers();
  if (!_containers || _containers.length === 0 || !id) {
    return '';
  }
  const _lot = _containers.find((c) => c.id === id);
  return _lot.name;
};

export function Create2DArray(rows) {
  const arr = [];
  for (let i = 0; i < rows; i++) {
    arr[i] = [];
  }
  return arr;
}
export async function readFileAsBase64String(file: any, callBack) {
  const reader = new FileReader();
  reader.onload = async (e) => {
    let base64String = e.target.result.toString();
    base64String = base64String.split(',')[1];
    callBack(base64String);
  };
  reader.readAsDataURL(file);
}

export function getFileURL(fileData: string, fileType) {
  const byteCharacters = atob(fileData);

  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);

  const file = new Blob([byteArray], { type: fileType });
  const url = URL.createObjectURL(file);
  return url;
}

export function groupBySingleField(
  data: Array<any>,
  field: string,
): { data: Array<any>; collapse: boolean } {
  return data.reduce(function (r, a) {
    r[a[field]] = r[a[field]] || {
      data: [],
      collapse: false,
    };
    r[a[field]]['data'].push(a);
    return r;
  }, Object.create(null));
}

export function groupByMultipleFields(data, ...fields) {
  if (fields.length === 0) {
    return;
  }
  let newData = {};
  const [field] = fields;
  newData = groupBySingleField(data, field);
  const remainingFields = fields.slice(1);
  if (remainingFields.length > 0) {
    Object.keys(newData).forEach((key) => {
      newData[key] = {
        data: groupByMultipleFields(newData[key]['data'], ...remainingFields),
        collapse: false,
      };
    });
  }
  return newData;
}

export function convertRGBToHex(rgbColor) {
  const rgbColorArray = rgbColor.split(', ');
  if (rgbColorArray.length === 1) {
    return rgbColor;
  }

  let r = (+rgbColorArray[0]).toString(16),
    g = (+rgbColorArray[1]).toString(16),
    b = (+rgbColorArray[2]).toString(16);

  if (r.length == 1) {
    r = '0' + r;
  }
  if (g.length == 1) {
    g = '0' + g;
  }
  if (b.length == 1) {
    b = '0' + b;
  }

  return '#' + r + g + b;
}

export const getSystemLanguage = () => {
  return navigator.languages && navigator.languages.length
    ? navigator.languages[0]
    : navigator.language;
};

export const isCustomScheduleSelected = (
  imagingSchedule: ExperimentInfoImagingScheduleDto,
) => {
  let isChoosedCustom = false;
  for (let i = 0; i < imagingSchedule.imagingSchedules.length; i++) {
    if (
      imagingSchedule.selectedImagingScheduleId ===
        imagingSchedule.imagingSchedules[i].id &&
      imagingSchedule.imagingSchedules[i].name === 'Custom'
    ) {
      isChoosedCustom = true;
    }
  }
  return isChoosedCustom;
};
export const treeIconColors = {
  Folder: '#CE873A',
  Project: '#0059CC',
  SynchrotronTripv4: 'none',
};
const treeIconNames = {
  [ETreeNodeTypes.ProjectFolder]: 'Folder',
  [ETreeNodeTypes.ProjectsFolder]: 'Folder',
  [ETreeNodeTypes.ScreensFolder]: 'Folder',
  [ETreeNodeTypes.Protein]: 'ProteinFormulation',
  [ETreeNodeTypes.Screen]: 'Screen',
  [ETreeNodeTypes.Experiment]: 'Experiment',
  [ETreeNodeTypes.Project]: 'Project',
  [ETreeNodeTypes.Seed]: 'Seed',
  [ETreeNodeTypes.Projects]: 'Projects',
  [ETreeNodeTypes.Screens]: 'Screens',
  [ETreeNodeTypes.QuickAccess]: 'QuickAccess',
  [ETreeNodeTypes.CrystalTrackv4]: 'CrystalTrack',
  [ETreeNodeTypes.SynchrotronTripv4]: 'SynchrotronTripv4',
  [ETreeNodeTypes.SynchrotronTripv4Folder]: 'Folder',
  [ETreeNodeTypes.CrystalContainer]: 'Setup/xtal',
  [ETreeNodeTypes.Scheduling]: 'Inspections',
  [ETreeNodeTypes.Containers]: 'Setup/Plate',
  [ETreeNodeTypes.Ingredients]: 'Setup/Reagents',
  [ETreeNodeTypes.CaptureProfiles]: 'Setup/Camera',
  [ETreeNodeTypes.Config]: 'Setup/Settings',
  [ETreeNodeTypes.TextSearch]: 'Search',
  [ETreeNodeTypes.FragmentScreen]: 'Fragment',
  [ETreeNodeTypes.SearchAnalysis]: 'SearchAnalysis',
  [ETreeNodeTypes.Searches]: 'Searches',
  [ETreeNodeTypes.SearchesFolder]: 'Folder',
  [ETreeNodeTypes.RecycleBin]: 'RecycleBin',
};
export const getTreeNodeImageProps = (node: TreeNodeDTO) => {
  let iconName;
  switch (node.type) {
    case ETreeNodeTypes.Experiment:
      iconName = `${treeIconNames[node.type]}_${node?.extra?.state || 'Designed'}`;
      break;
    case ETreeNodeTypes.SynchrotronTripv4:
      iconName = `${treeIconNames[node.type]}_${node?.extra?.status || 'Close'}`;
      break;
    case ETreeNodeTypes.Protein:
      iconName = node?.extra?.isVariantExist
        ? `${treeIconNames[node.type]}_Variants`
        : treeIconNames[node.type];
      break;
    default:
      iconName = treeIconNames[node.type];
      break;
  }
  const iconProps: RMSvgIconProps = {
    name: (iconName as RMSvgIconNames) ?? 'Folder',
    ...(node.type === ETreeNodeTypes.Experiment && { subfolder: 'Tree' }),
    ...(node.type === ETreeNodeTypes.FragmentScreen && { subfolder: 'Fragment' }),
    fill:
      treeIconColors[treeIconNames[node.type]] && node.allowEdits
        ? treeIconColors[treeIconNames[node.type]]
        : treeNodeIconsColor,
  };
  return iconProps;
};

export const getNodeInfoDetails = (data, type) => {
  if (type === ETreeNodeTypes.Protein) {
    const nodeInfoDetails = [
      { labelName: 'Created', info: data?.nodeInfo.dateCreated },
      {
        labelName: 'Owner',
        info: data?.nodeInfo.proteinFormulationIngredient[0]?.ownerName,
      },
      { labelName: 'Ingredient', info: data?.nodeInfo.proteinFormulationIngredient },
    ];
    return nodeInfoDetails;
  } else if (type === ETreeNodeTypes.Seed) {
    const nodeInfoDetails = [
      { labelName: 'Created', info: data?.dateCreated },
      { labelName: 'From Experiment', info: data?.expName },
      { labelName: 'Well', info: data?.wellPlace },
      { labelName: 'Stabalizing Ing.', info: data?.stabalizingIngredients },
      { labelName: 'Use Well Solution', info: data?.useWellSolution },
    ];
    return nodeInfoDetails;
  } else if (type === ETreeNodeTypes.Experiment || type === ETreeNodeTypes.Screen) {
    const nodeInfoDetails = [
      { labelName: 'Created', info: data.dateCreated },
      { labelName: 'Owner', info: data.userName },
      { labelName: 'Plate Type', info: data.plateType },
      { labelName: 'ID', info: data.id },
      { labelName: 'Plate Rows', info: data.plateRowCount },
      { labelName: 'Plate Columns', info: data.plateColCount },
      { labelName: 'Setup Temp.', info: data.setupTemperature },
      { labelName: 'Incubation Temp.', info: data.incubTemperature },
      { labelName: 'Reducing Agent', info: data.reducingAgent },
      { labelName: 'Notebook Page', info: data.noteBookPageNum },
      { labelName: 'Notes', info: data.notes },
    ];
    return nodeInfoDetails;
  } else if (type === ETreeNodeTypes.FragmentScreen) {
    const nodeInfoDetails = [
      {
        labelName: 'Condition Number',
        title: 'Condition Number',
        info: data.conditionNumber,
      },
      { labelName: 'Fragment Id', title: 'Fragment Id', info: data.fragmentId },
      { labelName: 'Fragment Name', title: 'Fragment Name', info: data.fragmentName },
      { labelName: 'Size', title: 'Size', info: data.size },
      { labelName: 'Working Conc.', title: 'Working Conc.', info: data.workingConc },
      { labelName: 'Notes', title: 'Notes', info: data.notes },
    ];
    return nodeInfoDetails;
  }
};

export const getNestedObject = (obj, currentKey, ...remaingKeysInOrder) => {
  if (obj === undefined) {
    return 'notfound';
  }
  if (
    remaingKeysInOrder.length === 0 &&
    Object.prototype.hasOwnProperty.call(obj, currentKey)
  ) {
    return obj[currentKey];
  }
  // @ts-ignore
  return getNestedObject(obj[currentKey], ...remaingKeysInOrder);
};

export const validateEmail = (email = ''): boolean => {
  const regex = new RegExp(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/);
  return regex.test(email);
};

const treeService = new TreeService();
export const getAbsoluteRootTreeNode = async (nodeId: number) => {
  return await treeService.getAbsoluteRootIdByChildId(nodeId);
};

export async function launchExpPrintBarCodeRequest(
  expIDs: number[],
): Promise<PrintBarcodeApiResponse[]> {
  const printBarcodeClient: PrintBarcodeClient = ModelFactory(
    PrintBarcodeClient,
    false,
    true,
  );
  const printStatus: PrintBarcodeApiResponse[] =
    await printBarcodeClient.performExpBarcodPrinting(expIDs);

  return printStatus;
}

export async function launchPlatePrintBarCodeRequest(
  plateIds: number[],
): Promise<PrintBarcodeApiResponse[]> {
  const printBarcodeClient: PrintBarcodeClient = ModelFactory(
    PrintBarcodeClient,
    false,
    true,
  );
  return await printBarcodeClient.performPlateBarcodesPrinting(plateIds);
}

export function getInspectionIdFromGroupByPlateId(
  wellDetails: WellDetailsStates,
  plateId: number,
): number {
  return wellDetails.inspectionSet?.groupedInspections.find(
    (gi) => gi.plateId === plateId,
  )?.imageBatchId;
}

export function GetBlankImageBase64() {
  return 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
}
export async function addImage(imageData) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = imageData;
  });
}

export function getAppMajorVersion(version: string) {
  const v = version.split('.');
  return v[0] + '.' + v[1];
}

export const downloadFileSaveAs = async (
  fileResponse: FileResponse,
  fileType: string,
  suggestedName: string,
) => {
  try {
    const options = {
      types: [
        {
          description: fileType,
          accept: {
            'text/plain': [fileType],
          },
        },
      ],
      excludeAcceptAllOption: true,
      suggestedName: suggestedName,
    };

    const blob = new Blob([fileResponse.data]);
    const handle = await window.showSaveFilePicker(options);
    const writable = await handle.createWritable();
    await writable.write(blob);
    await writable.close();
    return;
  } catch (err) {
    if (err.name !== 'AbortError') {
      console.error(err.name, err.message);
      return;
    }
  }
};

export const getRequiredFiledsError = (object, requiredFields: { accessor; title }[]) => {
  const emptyFields = requiredFields.filter((field) => !object[field.accessor]);

  if (emptyFields.length) {
    const errorMessage = emptyFields
      .map((field, index) => `${index + 1}) ${field.title}`)
      .join('\n');

    return `The following fields are required:\n${errorMessage}`;
  }

  return '';
};

export const isValidNumber = (number: number) => {
  return number !== null && number !== undefined && !isNaN(parseFloat(number + ''));
};
export const downloadFile = async (fileResponse: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(new Blob([fileResponse]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
};
export function getDateISOFormat(date: Date) {
  if (!date) {
    return;
  }
  const d = new Date(date);
  return (
    d.getFullYear() +
    '-' +
    ('0' + (d.getMonth() + 1)).slice(-2) +
    '-' +
    ('0' + d.getDate()).slice(-2)
  );
}

export const hasAnyPropertyOfObjectValue = (obj): boolean => {
  for (const key in obj) {
    if (obj.hasOwnProperty(key) && obj[key] !== undefined && obj[key] !== null) {
      return true;
    }
  }
  return false;
};

export const browseFileHandler = (e) => {
  const file = e.target.files?.[0];
  if (file) {
    const reader = new FileReader();
    let dataArray;
    return new Promise((resolve) => {
      reader.onloadend = () => {
        const fileContent = reader?.result as any;
        dataArray = fileContent.split('\r\n').map((line) => line.split(','));
        resolve({ data: dataArray, name: file.name });
      };
      reader.readAsText(file);
    });
  }
};

export const isCtrlKeyPressed = (e: React.MouseEvent | React.KeyboardEvent): boolean => {
  if (
    navigator.userAgent.indexOf('Mac') != -1 ||
    navigator.appVersion.indexOf('Mac') != -1
  ) {
    return e.metaKey;
  }
  return e.ctrlKey;
};

export const setCookie = (key, value, options = {}) => {
  // Set the cookie with the provided key, value, and options
  Cookies.set(key, value, options);
};

export const removeCookie = (key, calledFrom, options = {}) => {
  // Remove the cookie with the provided key and options
  Cookies.remove(key, options);
};

export const getCookie = (key) => {
  // Retrieve the value of the cookie with the provided key
  return Cookies.get(key);
};

export const addToRecentDocuments = async (nodeId) => {
  const recentDocumentService = new RecentDocumentService();
  await recentDocumentService.saveDocumentToRecents(nodeId);
};

export const onlyUnique = (value, index, array) => {
  return array.indexOf(value) === index;
};

export const getLayerUserAction = (type: number) => {
  switch (type) {
    case LayerTypeDTO.Grid:
      return EUserAction.GridLayer;
    case LayerTypeDTO.ConditionList:
      return EUserAction.CustomLayer;
    case LayerTypeDTO.Random:
      return EUserAction.RandomLayer;
    case LayerTypeDTO.Protein:
      return EUserAction.ProtienLayer;
    case LayerTypeDTO.Seed:
      return EUserAction.SeedingLayer;
    case LayerTypeDTO.Screen:
      return EUserAction.ScreenLayer;
    case GridLayerTypes.SUBGRID:
      return EUserAction.GridLayer;
    case LayerTypeDTO.Fragment:
      return EUserAction.FragmentLayer;
    default:
      return EUserAction.CustomLayer;
  }
};

export const removeItemIfFoundElsePush = (array, item) => {
  if (array.indexOf(item) != -1) {
    array.splice(array.indexOf(item), 1);
  } else {
    array.push(item);
  }
};

export enum LayerDesignTabs {
  'Layers' = 0,
  'Protein' = 1,
}

export function base64ToImage(base64): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onerror = reject;
    img.src = base64;
    img.onload = () => resolve(img);
  });
}

export const sortArray = (arr: any[]) => {
  if (!arr) {
    return null;
  }

  return arr.sort((a, b) => {
    if (a == null && b == null) {
      return 0;
    }
    if (a == null) {
      return -1;
    }
    if (b == null) {
      return 1;
    }

    if (!isNaN(a) && !isNaN(b)) {
      return a - b;
    }

    return a.localeCompare(b);
  });
};

export const sortAlphanumericDescending = <T, K extends keyof T>(
  array: T[] | null | undefined,
  key: K,
): T[] => {
  if (!array || array.length === 0) {
    return [];
  }

  return [...array].sort((a, b) => {
    const valA = a && a[key] ? String(a[key]) : '';
    const valB = b && b[key] ? String(b[key]) : '';

    return valB.localeCompare(valA, undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  });
};

export const excludeKeyFromArrayAndStringify = <T extends Record<string, any>>(
  array: T[],
  key: string,
): any => {
  return JSON.stringify(
    array.map((obj) => {
      // eslint-disable-next-line
      const { [key]: excludedKey, ...rest } = obj;
      return rest;
    }),
  );
};

export const convertToCsv = (data, headerNames) => {
  const headerRow = headerNames.join(',') + '\n';
  const dataRows = data
    .map((row) => headerNames.map((header) => row[header]).join(','))
    .join('\n');
  const csvContent = headerRow + dataRows;
  return btoa(csvContent);
};

export const convertToTxt = (data, headerNames) => {
  const headerRow = `${headerNames.join(',')}\n`;
  const dataRows = data
    .map((row) => headerNames.map((header) => row[header]).join('\t'))
    .join('\n');
  const csvContent = headerRow + dataRows;
  return btoa(csvContent);
};

export const isDropModeUrl = () => {
  return window.location.pathname.split('/').pop() === 'drop' ? true : false;
};

export const isWellModeUrl = () => {
  return window.location.pathname.split('/').pop() === 'well' ? true : false;
};

export const isDropWellCompare = () => {
  return ['drop', 'well', 'compare'].includes(window.location.pathname.split('/').pop());
};

export const isRMWebLicensed = () => {
  return (
    getCookie('licensedFeatures')?.includes(EFeatureTypes.RockMakerWeb) ||
    getCookie('licensedFeatures')?.includes(EFeatureTypes.RockMakerLite) ||
    getCookie('licenseType') === 'Temporary'
  );
};

export const getAlertTypeByResponseType = (responseType: ResponseType): EAlertType => {
  switch (responseType) {
    case ResponseType.Warning:
      return EAlertType.WARNING;
    case ResponseType.Error:
    case ResponseType.Exception:
      return EAlertType.ERROR;
    case ResponseType.Success:
      return EAlertType.SUCCESS;
    default:
      return EAlertType.INFO;
  }
};

/**
 * Checks if the provided key is a single alphanumeric character (A-Z, a-z, or 0-9).
 *
 * @param key - The character to test.
 * @returns `true` if the key is a single uppercase letter, lowercase letter, or digit; otherwise, `false`.
 */
export const isUpperLowerAlphaNumeric = (key) => {
  return /^[A-Za-z0-9]$/.test(key);
};

export const clearUserDataAndLogout = async (
  redirect: boolean = true,
  postLoginRedirectTo?: string,
) => {
  console.info('Clearing user data and logging out...');

  const currentPath = window.location.pathname + window.location.search;
  const isOnLogin = window.location.pathname.startsWith('/login');
  const existingRedirectUrl = sessionStorage.getItem(REDIRECT_URL_KEY);
  const isOnAuthFlowPath = AUTH_FLOW_PATHS.some((path) =>
    window.location.pathname.startsWith(path),
  );

  if (isOnAuthFlowPath && !isOnLogin) {
    sessionStorage.removeItem(REDIRECT_URL_KEY);
  } else if (!isOnAuthFlowPath) {
    const targetPath = postLoginRedirectTo ?? currentPath;
    if (!existingRedirectUrl || existingRedirectUrl !== targetPath) {
      sessionStorage.setItem(REDIRECT_URL_KEY, targetPath);
    }
  }

  removeCookie('upgradeNotified', 'logout');
  //expire feature selection; RM or RMLite
  removeCookie(EFeatureTypes.SelectedFeature, 'logout');
  //expire the hybrid license to ask option at each login.
  removeCookie('selectedLicenseType', 'logout');
  removeCookie('selectedLicenseIndex', 'logout');

  const closeHubTask = connectionHubManager.closeConnection('notificationHub');
  await CacheHelper.delete('token');
  await CacheHelper.delete('X-MachineName');
  clearAuthStorage();
  localStorage.clear();
  await closeHubTask;

  if (redirect && !isOnLogin && navigateFunc) {
    navigateFunc('/login');
  }
};

export const validateVersion = (version: any): boolean => {
  const versionPattern =
    /^\d+\.\d+\.\d+(?:\.\d+)?(?:-[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?(?:\+[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?$/;
  return !!version && typeof version === 'string' && versionPattern.test(version.trim());
};

export const MaxNameLength = {
  TREE_NODE: 64,
  USERS: 100,
  SEED_FORMULATION_INGREDIENT: 50,
} as const;

export enum NavigationSource {
  Explorer = 'explorer',
}

export const delay = (durationMs) =>
  new Promise((resolve) => {
    setTimeout(() => resolve(true), durationMs);
  });

export const setSystemStateBusy = async () => {
  const connection = await connectionHubManager.getConnection(
    'notificationHub',
    '/notificationHub',
  );
  const stream = connection.stream<number>('AcquireSystemStateStream');
  return stream.subscribe({
    next: () => {},
    error: (e) => console.error(e),
    complete: () => {},
  });
};

export const navigationBlockWarningMessage = (
  <div className="font-roboto font-normal text-[14px] align-middle text-[#5D5D5D]">
    You are about to navigate to a different page. Any Modifications you have made will
    not be saved.
    <br />
    <br />
    Are you sure you want to leave this page?
  </div>
);

export const formatBytes = (bytes: number, decimals: number = 2): string => {
  if (!Number.isFinite(bytes) || bytes === 0) {
    return '0 Bytes';
  }

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export const getCurrentDateISOString = (): string => {
  return new Date().toISOString().slice(0, 10);
};

export const RoundValue = (value: number, precision: number = 1000): number => {
  return Math.round((value + Number.EPSILON) * precision) / precision;
};

export const GetNormalizedBatchloadEntryValue = (entry: Entry) => {
  return entry.value?.trim().toLowerCase();
};

export const isExpInfoPath = () => {
  const regex = /^\/?exp\/\d+\/info\/?$/;
  return regex.test(window.location.pathname);
};

export enum ErrorMessages {
  BAD_REQUEST = 'Request failed. Please check your network connection',
  TIMEOUT = 'Request timed out. Please try again.',
}

Array.prototype.swapItemsByIndex = function <T>(indexA: number, indexB: number): T[] {
  const updated = [...this] as T[];
  [updated[indexA], updated[indexB]] = [updated[indexB], updated[indexA]];
  return updated;
};

export const convertHexToRGBAString = (hex: string, alpha: number): string => {
  const trimmed = hex.replace('#', '');
  const isShorthand = trimmed.length === 3;
  const r = parseInt(isShorthand ? trimmed[0] + trimmed[0] : trimmed.slice(0, 2), 16);
  const g = parseInt(isShorthand ? trimmed[1] + trimmed[1] : trimmed.slice(2, 4), 16);
  const b = parseInt(isShorthand ? trimmed[2] + trimmed[2] : trimmed.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const getDateTime12HourFormat = (date: Date | null): string => {
  if (!date) {
    return '';
  }

  const d = date instanceof Date ? date : new Date(date);

  if (isNaN(d.getTime())) {
    return '';
  }

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');

  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const suffix = hours >= 12 ? 'PM' : 'AM';

  hours = hours % 12;
  hours = hours === 0 ? 12 : hours;
  const hoursStr = String(hours).padStart(2, '0');

  return `${year}-${month}-${day} ${hoursStr}:${minutes} ${suffix}`;
};

export const compareByNameWithNullsLast = (a: string | null, b: string | null) => {
  if (!a && !b) {
    return 0;
  } else if (!a) {
    return 1;
  } else if (!b) {
    return -1;
  }
  return a.localeCompare(b);
};

String.prototype.equalsIgnoreCase = function (value: string): boolean {
  return this.trim().toLowerCase() === value.trim().toLowerCase();
};

export const sortTableRows = <T extends Record<string, any>>(
  rows: T[],
  sortState: { column: string; type: FmlxTable.SortType } | null,
): T[] => {
  if (!sortState) {
    return rows;
  }
  const { column, type } = sortState;
  return [...rows].sort((a, b) => {
    const aValue = a[column];
    const bValue = b[column];
    if (aValue === bValue) {
      return 0;
    }
    const result = aValue > bValue ? 1 : -1;
    return type === FmlxTable.SortType.ASC ? result : -result;
  });
};
